import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Shield, Wallet, TrendingUp, TrendingDown, Copy,
  ArrowUpRight, ArrowDownRight, Eye, EyeOff, RefreshCw,
  Settings, LogOut, Bell, ChevronRight, Plus, Send as SendIcon, Download,
  X, AlertTriangle, CheckCircle, AlertCircle, History, Info,
  BarChart3, Lock, Sparkles, ArrowRight, Coins, Clock,
  Gift, Percent, DollarSign, Activity, Star, Flame,
  ExternalLink, ShoppingCart, Menu
} from 'lucide-react';
import { useAuth } from '../App';
import BrandLogo from '../components/BrandLogo';
import toast from 'react-hot-toast';
import './Dashboard.css';

// Crypto exchange platforms for buying/selling
// Using favicon.io and Google favicon as reliable sources
const cryptoExchanges = [
  { 
    id: 'transak', 
    name: 'Transak', 
    logo: '/wallet-icons/transak.jpg',
    fallbackLogo: '/wallet-icons/transak.jpg',
    description: 'Buy crypto with card or bank transfer',
    fees: '1.5%',
    paymentMethods: ['Card', 'Bank'],
    rating: 4.8,
    color: '#0066FF',
    getBuyUrl: (symbol, amount) => `https://global.transak.com/?cryptoCurrencyCode=${symbol}&fiatAmount=${amount}&fiatCurrency=USD`,
    getSellUrl: (symbol, amount) => `https://global.transak.com/?cryptoCurrencyCode=${symbol}&cryptoAmount=${amount}&isSell=true`
  },
  { 
    id: 'coinbase', 
    name: 'Coinbase', 
    logo: 'https://assets.coingecko.com/markets/images/23/large/Coinbase_Coin_Primary.png',
    fallbackLogo: 'https://www.google.com/s2/favicons?domain=coinbase.com&sz=128',
    description: 'Trusted by millions worldwide',
    fees: '1.49%',
    paymentMethods: ['Card', 'Bank', 'PayPal'],
    rating: 4.7,
    color: '#0052FF',
    getBuyUrl: (symbol) => `https://www.coinbase.com/buy/${symbol}`,
    getSellUrl: (symbol) => `https://www.coinbase.com/sell/${symbol}`
  },
  { 
    id: 'crypto.com', 
    name: 'Crypto.com', 
    logo: 'https://assets.coingecko.com/markets/images/589/large/crypto-com-exchange.png',
    fallbackLogo: 'https://www.google.com/s2/favicons?domain=crypto.com&sz=128',
    description: 'Low fees with CRO staking',
    fees: '0.4%',
    paymentMethods: ['Card', 'Bank', 'Apple Pay'],
    rating: 4.6,
    color: '#002D74',
    getBuyUrl: (symbol) => `https://crypto.com/exchange/trade/${symbol}_USDT`,
    getSellUrl: (symbol) => `https://crypto.com/exchange/trade/${symbol}_USDT`
  },
  { 
    id: 'binance', 
    name: 'Binance', 
    logo: 'https://assets.coingecko.com/markets/images/52/large/binance.jpg',
    fallbackLogo: 'https://www.google.com/s2/favicons?domain=binance.com&sz=128',
    description: 'World\'s largest crypto exchange',
    fees: '0.1%',
    paymentMethods: ['Card', 'Bank', 'P2P'],
    rating: 4.5,
    color: '#F3BA2F',
    getBuyUrl: (symbol) => `https://www.binance.com/en/crypto/buy/${symbol}`,
    getSellUrl: (symbol) => `https://www.binance.com/en/crypto/sell/${symbol}`
  },
  { 
    id: 'moonpay', 
    name: 'MoonPay', 
    logo: 'https://assets.coingecko.com/markets/images/602/large/moonpay.png',
    fallbackLogo: 'https://www.google.com/s2/favicons?domain=moonpay.com&sz=128',
    description: 'Simple way to buy crypto',
    fees: '4.5%',
    paymentMethods: ['Card', 'Apple Pay', 'Google Pay'],
    rating: 4.4,
    color: '#7C3AED',
    getBuyUrl: (symbol, amount) => `https://www.moonpay.com/buy/${symbol.toLowerCase()}?baseCurrencyAmount=${amount}`,
    getSellUrl: (symbol) => `https://www.moonpay.com/sell/${symbol.toLowerCase()}`
  },
  { 
    id: 'kraken', 
    name: 'Kraken', 
    logo: 'https://assets.coingecko.com/markets/images/29/large/kraken.jpg',
    fallbackLogo: 'https://www.google.com/s2/favicons?domain=kraken.com&sz=128',
    description: 'Secure & reliable since 2011',
    fees: '0.26%',
    paymentMethods: ['Bank', 'Wire'],
    rating: 4.6,
    color: '#5741D9',
    getBuyUrl: (symbol) => `https://www.kraken.com/sign-up?click_id=buy_${symbol.toLowerCase()}`,
    getSellUrl: (symbol) => `https://www.kraken.com/sign-up?click_id=sell_${symbol.toLowerCase()}`
  },
];

// Crypto names mapping for display
const cryptoNames = {
  BTC: 'Bitcoin', ETH: 'Ethereum', USDT: 'Tether', BNB: 'BNB', SOL: 'Solana',
  XRP: 'XRP', USDC: 'USD Coin', ADA: 'Cardano', DOGE: 'Dogecoin', TRX: 'Tron',
  LINK: 'Chainlink', MATIC: 'Polygon', DOT: 'Polkadot', LTC: 'Litecoin', 
  SHIB: 'Shiba Inu', DAI: 'Dai', UNI: 'Uniswap', WBTC: 'Wrapped Bitcoin',
  AVAX: 'Avalanche', ATOM: 'Cosmos', OP: 'Optimism', ARB: 'Arbitrum',
  XLM: 'Stellar', NEAR: 'NEAR Protocol', APT: 'Aptos', FIL: 'Filecoin',
  VET: 'VeChain', ALGO: 'Algorand', AAVE: 'Aave', EOS: 'EOS',
  SAND: 'The Sandbox', MANA: 'Decentraland', AXS: 'Axie Infinity', 
  XTZ: 'Tezos', THETA: 'Theta', FTM: 'Fantom', CRO: 'Cronos',
  EGLD: 'MultiversX', FLOW: 'Flow', GRT: 'The Graph', RUNE: 'THORChain',
  ZEC: 'Zcash', ENJ: 'Enjin Coin', CHZ: 'Chiliz', '1INCH': '1inch',
  LRC: 'Loopring', COMP: 'Compound', SNX: 'Synthetix', CRV: 'Curve DAO',
  MKR: 'Maker', PEPE: 'Pepe', SUI: 'Sui', SEI: 'Sei', INJ: 'Injective', IMX: 'Immutable', WIFI: 'WiFi Map'
};

// Available crypto assets configuration
const cryptoAssetConfig = {
  BTC: { name: 'Bitcoin', color: '#F7931A', icon: '/Crypto-logos/Bitcoin-Logo.webp' },
  ETH: { name: 'Ethereum', color: '#627EEA', icon: '/Crypto-logos/Ethereum-logo.webp' },
  SOL: { name: 'Solana', color: '#00FFA3', icon: '/Crypto-logos/Solana-logo.webp' },
  BNB: { name: 'BNB', color: '#F3BA2F', icon: '/Crypto-logos/bnb-logo.webp' },
  MATIC: { name: 'Polygon', color: '#8247E5', icon: '/Crypto-logos/matic-token-icon.webp' },
  XRP: { name: 'Ripple', color: '#23292F', icon: '/Crypto-logos/XRP-logo.webp' },
  ADA: { name: 'Cardano', color: '#0033AD', icon: '/Crypto-logos/Cardano-logo.webp' },
  DOGE: { name: 'Dogecoin', color: '#C3A634', icon: '/Crypto-logos/Doge-Logo.webp' },
  DOT: { name: 'Polkadot', color: '#E6007A', icon: '/Crypto-logos/Polkadot-logo.webp' },
  LINK: { name: 'Chainlink', color: '#2A5ADA', icon: '/Crypto-logos/Chainlink-logo.webp' },
  LTC: { name: 'Litecoin', color: '#BFBBBB', icon: '/Crypto-logos/litecoin.webp' },
  USDT: { name: 'Tether', color: '#26A17B', icon: '/Crypto-logos/SizeXL-TypeCoin-AssetUSDT.webp' },
  USDC: { name: 'USD Coin', color: '#2775CA', icon: '/Crypto-logos/USD_Coin_icon.webp' },
  UNI: { name: 'Uniswap', color: '#FF007A', icon: '/Crypto-logos/Uniswap-logo.webp' },
  SHIB: { name: 'Shiba Inu', color: '#FFA409', icon: '/Crypto-logos/shiba.webp' },
  TRX: { name: 'Tron', color: '#FF0013', icon: '/Crypto-logos/Tron-Logo.webp' },
  DAI: { name: 'Dai', color: '#F5AC37', icon: '/Crypto-logos/Dai-logo.webp' },
  WBTC: { name: 'Wrapped BTC', color: '#F09242', icon: '/Crypto-logos/Wrapped-Bitcoin-logo-1.webp' },
  ARB: { name: 'Arbitrum', color: '#28A0F0', icon: '/Crypto-logos/Arbitrum-logo.webp' },
  OP: { name: 'Optimism', color: '#FF0420', icon: '/Crypto-logos/Optimism.webp' },
  WIFI: { name: 'WiFi Map', color: '#2F80ED', icon: 'https://coin-images.coingecko.com/coins/images/29533/small/IMG_4643.png?1696528475' },
};

// Empty default - new users start with $0 until admin updates their assets
const defaultAssets = {};

// Bitcoin price history (simulated 7 days)
const generateChartData = () => {
  const basePrice = 104500;
  return Array.from({ length: 24 }, (_, i) => ({
    time: i,
    price: basePrice + Math.sin(i * 0.5) * 2000 + Math.random() * 1000
  }));
};

// Staking pools data with platform options
const stakingPools = [
  { id: 1, asset: 'ETH', name: 'Ethereum', apy: 4.5, minStake: 0.1, totalStaked: '1.2M', icon: '/Crypto-logos/Ethereum-logo.webp', lockPeriod: 'Flexible', risk: 'Low' },
  { id: 2, asset: 'SOL', name: 'Solana', apy: 7.2, minStake: 1, totalStaked: '890K', icon: '/Crypto-logos/Solana-logo.webp', lockPeriod: '30 Days', risk: 'Medium' },
  { id: 3, asset: 'BNB', name: 'BNB Chain', apy: 5.8, minStake: 0.5, totalStaked: '650K', icon: '/Crypto-logos/bnb-logo.webp', lockPeriod: '14 Days', risk: 'Low' },
  { id: 4, asset: 'MATIC', name: 'Polygon', apy: 9.5, minStake: 100, totalStaked: '2.1M', icon: '/Crypto-logos/matic-token-icon.webp', lockPeriod: '60 Days', risk: 'Medium' },
  { id: 5, asset: 'AVAX', name: 'Avalanche', apy: 8.1, minStake: 5, totalStaked: '420K', icon: 'https://assets.coingecko.com/coins/images/12559/small/Avalanche_Circle_RedWhite_Trans.png', lockPeriod: '21 Days', risk: 'Medium' },
  { id: 6, asset: 'ADA', name: 'Cardano', apy: 5.2, minStake: 10, totalStaked: '1.8M', icon: '/Crypto-logos/Cardano-logo.webp', lockPeriod: 'Flexible', risk: 'Low' },
  { id: 7, asset: 'DOT', name: 'Polkadot', apy: 12.5, minStake: 1, totalStaked: '560K', icon: '/Crypto-logos/Polkadot-logo.webp', lockPeriod: '28 Days', risk: 'Medium' },
  { id: 8, asset: 'ATOM', name: 'Cosmos', apy: 18.2, minStake: 0.5, totalStaked: '340K', icon: 'https://assets.coingecko.com/coins/images/1481/small/cosmos_hub.png', lockPeriod: '21 Days', risk: 'Medium' },
  { id: 9, asset: 'NEAR', name: 'NEAR Protocol', apy: 10.5, minStake: 1, totalStaked: '280K', icon: 'https://assets.coingecko.com/coins/images/10365/small/near.jpg', lockPeriod: 'Flexible', risk: 'Medium' },
  { id: 10, asset: 'FTM', name: 'Fantom', apy: 14.8, minStake: 10, totalStaked: '150K', icon: 'https://assets.coingecko.com/coins/images/4001/small/Fantom_round.png', lockPeriod: '14 Days', risk: 'High' },
];

// Staking platforms with their URLs
const stakingPlatforms = [
  {
    id: 'lido',
    name: 'Lido',
    logo: 'https://assets.coingecko.com/coins/images/13573/small/Lido_DAO.png',
    description: 'Liquid staking for ETH, SOL, MATIC',
    apy: '+0.2% bonus',
    supportedAssets: ['ETH', 'SOL', 'MATIC'],
    color: '#00A3FF',
    getStakeUrl: (asset) => `https://stake.lido.fi/?ref=${asset.toLowerCase()}`,
    getClaimUrl: () => `https://stake.lido.fi/withdrawals`,
    getUnstakeUrl: () => `https://stake.lido.fi/withdrawals/request`
  },
  {
    id: 'binance',
    name: 'Binance Earn',
    logo: 'https://assets.coingecko.com/markets/images/52/small/binance.jpg',
    description: 'Flexible & locked staking',
    apy: 'Variable APY',
    supportedAssets: ['ETH', 'SOL', 'BNB', 'MATIC', 'AVAX', 'ADA', 'DOT', 'ATOM', 'NEAR', 'FTM'],
    color: '#F3BA2F',
    getStakeUrl: (asset) => `https://www.binance.com/en/earn/${asset}`,
    getClaimUrl: () => `https://www.binance.com/en/my/earn/history/staking`,
    getUnstakeUrl: () => `https://www.binance.com/en/my/earn/history/staking`
  },
  {
    id: 'coinbase',
    name: 'Coinbase',
    logo: 'https://assets.coingecko.com/markets/images/23/large/Coinbase_Coin_Primary.png',
    description: 'Simple staking rewards',
    apy: 'Up to 6%',
    supportedAssets: ['ETH', 'SOL', 'ADA', 'ATOM', 'DOT'],
    color: '#0052FF',
    getStakeUrl: (asset) => `https://www.coinbase.com/earn/staking/${asset.toLowerCase()}`,
    getClaimUrl: () => `https://www.coinbase.com/accounts`,
    getUnstakeUrl: () => `https://www.coinbase.com/accounts`
  },
  {
    id: 'kraken',
    name: 'Kraken',
    logo: 'https://assets.coingecko.com/markets/images/29/large/kraken.jpg',
    description: 'Secure staking platform',
    apy: 'Up to 20%',
    supportedAssets: ['ETH', 'SOL', 'DOT', 'ATOM', 'ADA', 'NEAR', 'FTM'],
    color: '#5741D9',
    getStakeUrl: (asset) => `https://www.kraken.com/features/staking-coins/${asset.toLowerCase()}`,
    getClaimUrl: () => `https://www.kraken.com/u/staking/rewards`,
    getUnstakeUrl: () => `https://www.kraken.com/u/staking/unstake`
  },
  {
    id: 'rocketpool',
    name: 'Rocket Pool',
    logo: 'https://assets.coingecko.com/coins/images/20764/small/reth.png',
    description: 'Decentralized ETH staking',
    apy: '~4.5%',
    supportedAssets: ['ETH'],
    color: '#FF6E30',
    getStakeUrl: () => `https://stake.rocketpool.net/`,
    getClaimUrl: () => `https://stake.rocketpool.net/`,
    getUnstakeUrl: () => `https://stake.rocketpool.net/`
  },
  {
    id: 'marinade',
    name: 'Marinade Finance',
    logo: 'https://assets.coingecko.com/coins/images/18867/small/marinade.PNG',
    description: 'Liquid staking for Solana',
    apy: '~7%',
    supportedAssets: ['SOL'],
    color: '#C8E6C9',
    getStakeUrl: () => `https://marinade.finance/app/stake/`,
    getClaimUrl: () => `https://marinade.finance/app/`,
    getUnstakeUrl: () => `https://marinade.finance/app/unstake/`
  },
];

// Trading pairs initial data (will be updated with live prices) - 50+ cryptocurrencies
const initialTradingPairs = [
  // Top 10 by Market Cap
  { symbol: 'BTC/USDT', base: 'BTC', quote: 'USDT', price: 0, change: 0, high: 0, low: 0, volume: '0', icon: '/Crypto-logos/Bitcoin-Logo.webp', coingeckoId: 'bitcoin' },
  { symbol: 'ETH/USDT', base: 'ETH', quote: 'USDT', price: 0, change: 0, high: 0, low: 0, volume: '0', icon: '/Crypto-logos/Ethereum-logo.webp', coingeckoId: 'ethereum' },
  { symbol: 'USDT/USD', base: 'USDT', quote: 'USD', price: 0, change: 0, high: 0, low: 0, volume: '0', icon: '/Crypto-logos/SizeXL-TypeCoin-AssetUSDT.webp', coingeckoId: 'tether' },
  { symbol: 'BNB/USDT', base: 'BNB', quote: 'USDT', price: 0, change: 0, high: 0, low: 0, volume: '0', icon: '/Crypto-logos/bnb-logo.webp', coingeckoId: 'binancecoin' },
  { symbol: 'SOL/USDT', base: 'SOL', quote: 'USDT', price: 0, change: 0, high: 0, low: 0, volume: '0', icon: '/Crypto-logos/Solana-logo.webp', coingeckoId: 'solana' },
  { symbol: 'XRP/USDT', base: 'XRP', quote: 'USDT', price: 0, change: 0, high: 0, low: 0, volume: '0', icon: '/Crypto-logos/XRP-logo.webp', coingeckoId: 'ripple' },
  { symbol: 'USDC/USD', base: 'USDC', quote: 'USD', price: 0, change: 0, high: 0, low: 0, volume: '0', icon: '/Crypto-logos/USD_Coin_icon.webp', coingeckoId: 'usd-coin' },
  { symbol: 'ADA/USDT', base: 'ADA', quote: 'USDT', price: 0, change: 0, high: 0, low: 0, volume: '0', icon: '/Crypto-logos/Cardano-logo.webp', coingeckoId: 'cardano' },
  { symbol: 'DOGE/USDT', base: 'DOGE', quote: 'USDT', price: 0, change: 0, high: 0, low: 0, volume: '0', icon: '/Crypto-logos/Doge-Logo.webp', coingeckoId: 'dogecoin' },
  { symbol: 'TRX/USDT', base: 'TRX', quote: 'USDT', price: 0, change: 0, high: 0, low: 0, volume: '0', icon: '/Crypto-logos/Tron-Logo.webp', coingeckoId: 'tron' },
  
  // 11-20
  { symbol: 'LINK/USDT', base: 'LINK', quote: 'USDT', price: 0, change: 0, high: 0, low: 0, volume: '0', icon: '/Crypto-logos/Chainlink-logo.webp', coingeckoId: 'chainlink' },
  { symbol: 'MATIC/USDT', base: 'MATIC', quote: 'USDT', price: 0, change: 0, high: 0, low: 0, volume: '0', icon: '/Crypto-logos/matic-token-icon.webp', coingeckoId: 'matic-network' },
  { symbol: 'DOT/USDT', base: 'DOT', quote: 'USDT', price: 0, change: 0, high: 0, low: 0, volume: '0', icon: '/Crypto-logos/Polkadot-logo.webp', coingeckoId: 'polkadot' },
  { symbol: 'LTC/USDT', base: 'LTC', quote: 'USDT', price: 0, change: 0, high: 0, low: 0, volume: '0', icon: '/Crypto-logos/litecoin.webp', coingeckoId: 'litecoin' },
  { symbol: 'SHIB/USDT', base: 'SHIB', quote: 'USDT', price: 0, change: 0, high: 0, low: 0, volume: '0', icon: '/Crypto-logos/shiba.webp', coingeckoId: 'shiba-inu' },
  { symbol: 'DAI/USDT', base: 'DAI', quote: 'USDT', price: 0, change: 0, high: 0, low: 0, volume: '0', icon: '/Crypto-logos/Dai-logo.webp', coingeckoId: 'dai' },
  { symbol: 'UNI/USDT', base: 'UNI', quote: 'USDT', price: 0, change: 0, high: 0, low: 0, volume: '0', icon: '/Crypto-logos/Uniswap-logo.webp', coingeckoId: 'uniswap' },
  { symbol: 'WBTC/USDT', base: 'WBTC', quote: 'USDT', price: 0, change: 0, high: 0, low: 0, volume: '0', icon: '/Crypto-logos/Wrapped-Bitcoin-logo-1.webp', coingeckoId: 'wrapped-bitcoin' },
  { symbol: 'AVAX/USDT', base: 'AVAX', quote: 'USDT', price: 0, change: 0, high: 0, low: 0, volume: '0', icon: 'https://assets.coingecko.com/coins/images/12559/small/Avalanche_Circle_RedWhite_Trans.png', coingeckoId: 'avalanche-2' },
  { symbol: 'ATOM/USDT', base: 'ATOM', quote: 'USDT', price: 0, change: 0, high: 0, low: 0, volume: '0', icon: 'https://assets.coingecko.com/coins/images/1481/small/cosmos_hub.png', coingeckoId: 'cosmos' },
  
  // 21-30
  { symbol: 'OP/USDT', base: 'OP', quote: 'USDT', price: 0, change: 0, high: 0, low: 0, volume: '0', icon: '/Crypto-logos/Optimism.webp', coingeckoId: 'optimism' },
  { symbol: 'ARB/USDT', base: 'ARB', quote: 'USDT', price: 0, change: 0, high: 0, low: 0, volume: '0', icon: '/Crypto-logos/Arbitrum-logo.webp', coingeckoId: 'arbitrum' },
  { symbol: 'XLM/USDT', base: 'XLM', quote: 'USDT', price: 0, change: 0, high: 0, low: 0, volume: '0', icon: 'https://assets.coingecko.com/coins/images/100/small/Stellar_symbol_black_RGB.png', coingeckoId: 'stellar' },
  { symbol: 'NEAR/USDT', base: 'NEAR', quote: 'USDT', price: 0, change: 0, high: 0, low: 0, volume: '0', icon: 'https://assets.coingecko.com/coins/images/10365/small/near.jpg', coingeckoId: 'near' },
  { symbol: 'APT/USDT', base: 'APT', quote: 'USDT', price: 0, change: 0, high: 0, low: 0, volume: '0', icon: 'https://assets.coingecko.com/coins/images/26455/small/aptos_round.png', coingeckoId: 'aptos' },
  { symbol: 'FIL/USDT', base: 'FIL', quote: 'USDT', price: 0, change: 0, high: 0, low: 0, volume: '0', icon: 'https://assets.coingecko.com/coins/images/12817/small/filecoin.png', coingeckoId: 'filecoin' },
  { symbol: 'VET/USDT', base: 'VET', quote: 'USDT', price: 0, change: 0, high: 0, low: 0, volume: '0', icon: 'https://assets.coingecko.com/coins/images/1167/small/VET_Token_Icon.png', coingeckoId: 'vechain' },
  { symbol: 'ALGO/USDT', base: 'ALGO', quote: 'USDT', price: 0, change: 0, high: 0, low: 0, volume: '0', icon: 'https://assets.coingecko.com/coins/images/4380/small/download.png', coingeckoId: 'algorand' },
  { symbol: 'AAVE/USDT', base: 'AAVE', quote: 'USDT', price: 0, change: 0, high: 0, low: 0, volume: '0', icon: 'https://assets.coingecko.com/coins/images/12645/small/AAVE.png', coingeckoId: 'aave' },
  { symbol: 'EOS/USDT', base: 'EOS', quote: 'USDT', price: 0, change: 0, high: 0, low: 0, volume: '0', icon: 'https://assets.coingecko.com/coins/images/738/small/eos-eos-logo.png', coingeckoId: 'eos' },
  
  // 31-40
  { symbol: 'SAND/USDT', base: 'SAND', quote: 'USDT', price: 0, change: 0, high: 0, low: 0, volume: '0', icon: 'https://assets.coingecko.com/coins/images/12129/small/sandbox_logo.jpg', coingeckoId: 'the-sandbox' },
  { symbol: 'MANA/USDT', base: 'MANA', quote: 'USDT', price: 0, change: 0, high: 0, low: 0, volume: '0', icon: 'https://assets.coingecko.com/coins/images/878/small/decentraland-mana.png', coingeckoId: 'decentraland' },
  { symbol: 'AXS/USDT', base: 'AXS', quote: 'USDT', price: 0, change: 0, high: 0, low: 0, volume: '0', icon: 'https://assets.coingecko.com/coins/images/13029/small/axie_infinity_logo.png', coingeckoId: 'axie-infinity' },
  { symbol: 'XTZ/USDT', base: 'XTZ', quote: 'USDT', price: 0, change: 0, high: 0, low: 0, volume: '0', icon: 'https://assets.coingecko.com/coins/images/976/small/Tezos-logo.png', coingeckoId: 'tezos' },
  { symbol: 'THETA/USDT', base: 'THETA', quote: 'USDT', price: 0, change: 0, high: 0, low: 0, volume: '0', icon: 'https://assets.coingecko.com/coins/images/2538/small/theta-token-logo.png', coingeckoId: 'theta-token' },
  { symbol: 'FTM/USDT', base: 'FTM', quote: 'USDT', price: 0, change: 0, high: 0, low: 0, volume: '0', icon: 'https://assets.coingecko.com/coins/images/4001/small/Fantom_round.png', coingeckoId: 'fantom' },
  { symbol: 'CRO/USDT', base: 'CRO', quote: 'USDT', price: 0, change: 0, high: 0, low: 0, volume: '0', icon: 'https://assets.coingecko.com/coins/images/7310/small/cro_token_logo.png', coingeckoId: 'crypto-com-chain' },
  { symbol: 'EGLD/USDT', base: 'EGLD', quote: 'USDT', price: 0, change: 0, high: 0, low: 0, volume: '0', icon: 'https://assets.coingecko.com/coins/images/12335/small/egld-token-logo.png', coingeckoId: 'elrond-erd-2' },
  { symbol: 'FLOW/USDT', base: 'FLOW', quote: 'USDT', price: 0, change: 0, high: 0, low: 0, volume: '0', icon: 'https://assets.coingecko.com/coins/images/13446/small/5f6294c0c7a8cda55cb1c936_Flow_Wordmark.png', coingeckoId: 'flow' },
  { symbol: 'GRT/USDT', base: 'GRT', quote: 'USDT', price: 0, change: 0, high: 0, low: 0, volume: '0', icon: 'https://assets.coingecko.com/coins/images/13397/small/Graph_Token.png', coingeckoId: 'the-graph' },
  
  // 41-50
  { symbol: 'RUNE/USDT', base: 'RUNE', quote: 'USDT', price: 0, change: 0, high: 0, low: 0, volume: '0', icon: 'https://assets.coingecko.com/coins/images/6595/small/Rune200x200.png', coingeckoId: 'thorchain' },
  { symbol: 'ZEC/USDT', base: 'ZEC', quote: 'USDT', price: 0, change: 0, high: 0, low: 0, volume: '0', icon: 'https://assets.coingecko.com/coins/images/486/small/circle-zcash-color.png', coingeckoId: 'zcash' },
  { symbol: 'ENJ/USDT', base: 'ENJ', quote: 'USDT', price: 0, change: 0, high: 0, low: 0, volume: '0', icon: 'https://assets.coingecko.com/coins/images/1102/small/enjin-coin-logo.png', coingeckoId: 'enjincoin' },
  { symbol: 'CHZ/USDT', base: 'CHZ', quote: 'USDT', price: 0, change: 0, high: 0, low: 0, volume: '0', icon: 'https://assets.coingecko.com/coins/images/8834/small/CHZ_Token_updated.png', coingeckoId: 'chiliz' },
  { symbol: '1INCH/USDT', base: '1INCH', quote: 'USDT', price: 0, change: 0, high: 0, low: 0, volume: '0', icon: 'https://assets.coingecko.com/coins/images/13469/small/1inch-token.png', coingeckoId: '1inch' },
  { symbol: 'LRC/USDT', base: 'LRC', quote: 'USDT', price: 0, change: 0, high: 0, low: 0, volume: '0', icon: 'https://assets.coingecko.com/coins/images/913/small/LRC.png', coingeckoId: 'loopring' },
  { symbol: 'COMP/USDT', base: 'COMP', quote: 'USDT', price: 0, change: 0, high: 0, low: 0, volume: '0', icon: 'https://assets.coingecko.com/coins/images/10775/small/COMP.png', coingeckoId: 'compound-governance-token' },
  { symbol: 'SNX/USDT', base: 'SNX', quote: 'USDT', price: 0, change: 0, high: 0, low: 0, volume: '0', icon: 'https://assets.coingecko.com/coins/images/3406/small/SNX.png', coingeckoId: 'havven' },
  { symbol: 'CRV/USDT', base: 'CRV', quote: 'USDT', price: 0, change: 0, high: 0, low: 0, volume: '0', icon: 'https://assets.coingecko.com/coins/images/12124/small/Curve.png', coingeckoId: 'curve-dao-token' },
  { symbol: 'MKR/USDT', base: 'MKR', quote: 'USDT', price: 0, change: 0, high: 0, low: 0, volume: '0', icon: 'https://assets.coingecko.com/coins/images/1364/small/Mark_Maker.png', coingeckoId: 'maker' },
  
  // 51-55 (Bonus)
  { symbol: 'PEPE/USDT', base: 'PEPE', quote: 'USDT', price: 0, change: 0, high: 0, low: 0, volume: '0', icon: 'https://assets.coingecko.com/coins/images/29850/small/pepe-token.jpeg', coingeckoId: 'pepe' },
  { symbol: 'SUI/USDT', base: 'SUI', quote: 'USDT', price: 0, change: 0, high: 0, low: 0, volume: '0', icon: 'https://assets.coingecko.com/coins/images/26375/small/sui_asset.jpeg', coingeckoId: 'sui' },
  { symbol: 'SEI/USDT', base: 'SEI', quote: 'USDT', price: 0, change: 0, high: 0, low: 0, volume: '0', icon: 'https://assets.coingecko.com/coins/images/28205/small/Sei_Logo_-_Transparent.png', coingeckoId: 'sei-network' },
  { symbol: 'INJ/USDT', base: 'INJ', quote: 'USDT', price: 0, change: 0, high: 0, low: 0, volume: '0', icon: 'https://assets.coingecko.com/coins/images/12882/small/Secondary_Symbol.png', coingeckoId: 'injective-protocol' },
  { symbol: 'IMX/USDT', base: 'IMX', quote: 'USDT', price: 0, change: 0, high: 0, low: 0, volume: '0', icon: 'https://assets.coingecko.com/coins/images/17233/small/immutableX-symbol-BLK-RGB.png', coingeckoId: 'immutable-x' },
  { symbol: 'WIFI/USDT', base: 'WIFI', quote: 'USDT', price: 0, change: 0, high: 0, low: 0, volume: '0', icon: 'https://coin-images.coingecko.com/coins/images/29533/small/IMG_4643.png?1696528475', coingeckoId: 'wifi' },
];

const DASHBOARD_MARKET_CACHE_KEY = 'web3ledger_dashboard_market_cache_v1';

const readDashboardMarketCache = () => {
  if (typeof window === 'undefined') return null;

  try {
    const raw = window.localStorage.getItem(DASHBOARD_MARKET_CACHE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.tradingPairs)) return null;

    return {
      tradingPairs: parsed.tradingPairs,
      btcPrice: Number.parseFloat(parsed.btcPrice) || 0,
      btcChange: Number.parseFloat(parsed.btcChange) || 0,
    };
  } catch (error) {
    console.warn('Unable to read dashboard market cache:', error);
    return null;
  }
};

const writeDashboardMarketCache = (payload) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      DASHBOARD_MARKET_CACHE_KEY,
      JSON.stringify({
        tradingPairs: payload.tradingPairs || [],
        btcPrice: payload.btcPrice || 0,
        btcChange: payload.btcChange || 0,
        updatedAt: Date.now(),
      })
    );
  } catch (error) {
    console.warn('Unable to write dashboard market cache:', error);
  }
};

const Dashboard = () => {
  const { 
    user, logoutUser, 
    getUserTransactions, getUserNotifications, 
    markNotificationRead, markAllNotificationsRead,
    addTransaction
  } = useAuth();
  const navigate = useNavigate();
  const cachedMarketData = React.useMemo(() => readDashboardMarketCache(), []);
  const [showBalance, setShowBalance] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('portfolio');
  const [showSendModal, setShowSendModal] = useState(false);
  const [showReceiveModal, setShowReceiveModal] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showMobileNav, setShowMobileNav] = useState(false);
  const [btcPrice, setBtcPrice] = useState(cachedMarketData?.btcPrice || 0);
  const [btcChange, setBtcChange] = useState(cachedMarketData?.btcChange || 0);
  const prevBtcPrice = useRef(cachedMarketData?.btcPrice || 0);
  const [chartData] = useState(generateChartData);
  // Trading state - with live prices
  const [tradingPairs, setTradingPairs] = useState(cachedMarketData?.tradingPairs || initialTradingPairs);
  const [selectedPair, setSelectedPair] = useState(
    cachedMarketData?.tradingPairs?.[0] || initialTradingPairs[0]
  );
  const [pricesLoading, setPricesLoading] = useState(!(cachedMarketData?.tradingPairs?.length > 0));
  const [orderType, setOrderType] = useState('market');
  const [orderSide, setOrderSide] = useState('buy');
  const [tradeAmount, setTradeAmount] = useState('');
  const [tradePrice, setTradePrice] = useState('');
  const [openOrders, setOpenOrders] = useState([]);
  const [tradeHistory] = useState([]);
  const isProcessingTrade = false; // Trade redirects to external platforms
  const [chartTimeframe, setChartTimeframe] = useState('24H');
  const [tradingChartTimeframe, setTradingChartTimeframe] = useState('1D');
  const [poolFilter, setPoolFilter] = useState('All');
  const [pairSearchTerm, setPairSearchTerm] = useState('');
  const [ordersTab, setOrdersTab] = useState('open');
  const [availableBalance] = useState({
    USDT: 10000,
    BTC: 0.5,
    ETH: 2.5,
    SOL: 15.0,
    BNB: 3.0,
    MATIC: 1200,
    WIFI: 5000
  });

  // Staking state
  const [selectedPool, setSelectedPool] = useState(null);
  const [stakeAmount, setStakeAmount] = useState('');
  const [showStakeModal, setShowStakeModal] = useState(false);
  const userStakes = React.useMemo(() => {
    if (!Array.isArray(user?.stakes)) return [];

    return user.stakes
      .map((stake) => ({
        poolId: Number(stake?.poolId) || 0,
        asset: String(stake?.asset || '').toUpperCase(),
        amount: Number(stake?.amount) || 0,
        startDate: String(stake?.startDate || ''),
        rewards: Number(stake?.rewards) || 0,
        apy: Number(stake?.apy) || 0
      }))
      .filter((stake) => stake.asset && stake.amount > 0);
  }, [user?.stakes]);
  const [showClaimModal, setShowClaimModal] = useState(false);
  const [showUnstakeModal, setShowUnstakeModal] = useState(false);
  const [selectedStake, setSelectedStake] = useState(null);
  const [claimAllMode, setClaimAllMode] = useState(false);

  // Buy/Sell Crypto state
  const [showCryptoSelectModal, setShowCryptoSelectModal] = useState(false);
  const [showBuyModal, setShowBuyModal] = useState(false);
  const [buyAsset, setBuyAsset] = useState(null);
  const [buyAmount, setBuyAmount] = useState('100');
  const [selectedExchange, setSelectedExchange] = useState(null);
  const [tradeAction, setTradeAction] = useState('buy'); // 'buy' or 'sell'

  const [sendForm, setSendForm] = useState({
    recipient: '',
    amount: '',
    asset: 'BTC'
  });

  const [showSyncPopup, setShowSyncPopup] = useState(false);
  const notificationsRef = useRef(null);
  const mobileNavRef = useRef(null);

  const userTransactions = user ? getUserTransactions(user.id) : [];
  const userNotifications = user ? getUserNotifications(user.id) : [];
  const unreadCount = userNotifications.filter(n => !n.read).length;

  // Get user's assets or use defaults
  const userAssets = user?.assets && Object.keys(user.assets).length > 0 ? user.assets : defaultAssets;

  // Build dynamic portfolio assets based on user's assets and live prices
  const portfolioAssets = React.useMemo(() => {
    // Price mapping from trading pairs
    const priceMap = {};
    tradingPairs.forEach(pair => {
      priceMap[pair.base] = { price: pair.price || 0, change: pair.change || 0 };
    });

    // Build portfolio from user's assets
    return Object.entries(userAssets)
      .filter(([symbol, balance]) => balance > 0 && cryptoAssetConfig[symbol])
      .map(([symbol, balance]) => {
        const config = cryptoAssetConfig[symbol];
        const priceData = priceMap[symbol] || { price: 0, change: 0 };
        const value = balance * priceData.price;
        
        return {
          symbol,
          name: config.name,
          balance,
          value,
          change: priceData.change,
          color: config.color,
          icon: config.icon
        };
      })
      .sort((a, b) => b.value - a.value); // Sort by value descending
  }, [userAssets, tradingPairs]);

  const totalAssetValue = portfolioAssets.reduce((acc, asset) => acc + asset.value, 0);
  const fiatBalance = Number.parseFloat(user?.balance) || 0;
  const totalValue = totalAssetValue + fiatBalance;
  const totalChange = portfolioAssets.length > 0 && totalAssetValue > 0
    ? portfolioAssets.reduce((acc, asset) => acc + (asset.change * (asset.value / totalAssetValue)), 0)
    : 0;
  const stakePriceMap = React.useMemo(() => {
    const map = {};
    tradingPairs.forEach((pair) => {
      if (pair?.base) {
        map[pair.base] = Number(pair.price) || 0;
      }
    });
    return map;
  }, [tradingPairs]);
  const totalStakingRewardsUsd = React.useMemo(() => (
    userStakes.reduce((sum, stake) => sum + (stake.rewards * (stakePriceMap[stake.asset] || 0)), 0)
  ), [userStakes, stakePriceMap]);
  const averageStakingApy = userStakes.length > 0
    ? userStakes.reduce((sum, stake) => sum + stake.apy, 0) / userStakes.length
    : 0;
  const bestStakingApy = userStakes.length > 0
    ? Math.max(...userStakes.map((stake) => stake.apy))
    : 0;

  // Fetch live crypto prices from CoinGecko
  useEffect(() => {
    const fetchLivePrices = async () => {
      try {
        setPricesLoading(true);
        // Fetch detailed market data for all 55+ trading pairs
        const ids = initialTradingPairs.map(p => p.coingeckoId).join(',');
        const response = await fetch(
          `https://api.coingecko.com/api/v3/coins/markets?vs_currency=usd&ids=${ids}&order=market_cap_desc&sparkline=false&price_change_percentage=24h&per_page=100`
        );
        const data = await response.json();
        
        if (data && Array.isArray(data)) {
          // Update trading pairs with live data
          const updatedPairs = initialTradingPairs.map(pair => {
            const coinData = data.find(coin => coin.id === pair.coingeckoId);
            if (coinData) {
              return {
                ...pair,
                price: coinData.current_price || 0,
                change: coinData.price_change_percentage_24h || 0,
                high: coinData.high_24h || 0,
                low: coinData.low_24h || 0,
                volume: formatVolume(coinData.total_volume || 0)
              };
            }
            return pair;
          });
          
          setTradingPairs(updatedPairs);
          
          // Update selected pair with fresh price data without overwriting user's selection
          setSelectedPair(prev => {
            const updatedSelected = updatedPairs.find(p => p.symbol === prev.symbol);
            return updatedSelected || updatedPairs[0] || prev;
          });
          
          // Update BTC price for the balance card
          let nextBtcPrice = btcPrice;
          let nextBtcChange = btcChange;
          const btcData = data.find(coin => coin.id === 'bitcoin');
          if (btcData) {
            nextBtcPrice = btcData.current_price || 0;
            if (prevBtcPrice.current > 0) {
              nextBtcChange = ((nextBtcPrice - prevBtcPrice.current) / prevBtcPrice.current) * 100;
            } else {
              nextBtcChange = btcData.price_change_percentage_24h || 0;
            }
            prevBtcPrice.current = nextBtcPrice;
            setBtcPrice(nextBtcPrice);
            setBtcChange(nextBtcChange);
          }

          writeDashboardMarketCache({
            tradingPairs: updatedPairs,
            btcPrice: nextBtcPrice,
            btcChange: nextBtcChange,
          });
        }
      } catch (error) {
        console.log('Error fetching live prices:', error);
      } finally {
        setPricesLoading(false);
      }
    };
    
    // Format volume to readable string
    const formatVolume = (volume) => {
      if (volume >= 1e9) return (volume / 1e9).toFixed(1) + 'B';
      if (volume >= 1e6) return (volume / 1e6).toFixed(0) + 'M';
      if (volume >= 1e3) return (volume / 1e3).toFixed(0) + 'K';
      return volume.toString();
    };
    
    fetchLivePrices();
    // Refresh prices every 30 seconds
    const interval = setInterval(fetchLivePrices, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close notifications & mobile nav when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showNotifications && notificationsRef.current && !notificationsRef.current.contains(e.target)) {
        setShowNotifications(false);
      }
      if (showMobileNav && mobileNavRef.current && !mobileNavRef.current.contains(e.target)) {
        setShowMobileNav(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showNotifications, showMobileNav]);

  // Show wallet sync popup for newly connected users
  useEffect(() => {
    const justConnected = sessionStorage.getItem('walletJustConnected');
    if (justConnected === 'true') {
      setShowSyncPopup(true);
      sessionStorage.removeItem('walletJustConnected');
    }
  }, []);

  const handleCopyWalletId = () => {
    navigator.clipboard.writeText(user?.walletId || '');
    toast.success('Copied!');
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await new Promise(resolve => setTimeout(resolve, 1500));
    setIsRefreshing(false);
    toast.success('Portfolio synced');
  };

  const handleLogout = async () => {
    await logoutUser();
    navigate('/');
    toast.success('Logged out');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleSend = () => {
    if (!sendForm.recipient || !sendForm.amount) {
      toast.error('Please fill in all fields');
      return;
    }

    const sendAmount = parseFloat(sendForm.amount);
    if (sendAmount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    const ownedAsset = portfolioAssets.find(a => a.symbol === sendForm.asset);
    if (!ownedAsset || ownedAsset.balance < sendAmount) {
      toast.error(`Insufficient ${sendForm.asset} balance`);
      return;
    }
    
    addTransaction({
      userId: user.id,
      type: 'send',
      asset: sendForm.asset,
      amount: parseFloat(sendForm.amount),
      from: user.walletId,
      to: sendForm.recipient,
      status: 'pending',
      hash: `0x${Math.random().toString(16).slice(2, 10)}...`
    });
    
    setShowSendModal(false);
    setSendForm({ recipient: '', amount: '', asset: 'BTC' });
    toast.success('Transaction submitted!');
  };

  const handleMarkAllRead = () => {
    markAllNotificationsRead(user.id);
    toast.success('Marked as read');
  };

  // Trading execution handler - Opens exchange selection modal
  const handleExecuteTrade = () => {
    if (!tradeAmount || parseFloat(tradeAmount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    const amount = parseFloat(tradeAmount);
    const total = amount * selectedPair.price;

    // Set up the buy/sell modal with the trading pair info
    setBuyAsset({
      symbol: selectedPair.base,
      name: cryptoNames[selectedPair.base] || selectedPair.base,
      icon: selectedPair.icon,
      price: selectedPair.price
    });
    setBuyAmount(total.toFixed(2)); // Set amount in USD
    setTradeAction(orderSide); // 'buy' or 'sell'
    setShowBuyModal(true);
  };


  // Cancel open order
  const handleCancelOrder = (orderId) => {
    setOpenOrders(prev => prev.filter(order => order.id !== orderId));
    toast.success('Order cancelled');
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'success': return <CheckCircle size={12} />;
      case 'warning': return <AlertTriangle size={12} />;
      case 'error': return <AlertCircle size={12} />;
      default: return <Bell size={18} />;
    }
  };

  // Generate a simple QR-like pattern from a string
  const generateQRPattern = (text) => {
    const str = text || 'default';
    const size = 11;
    const cells = [];
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = ((hash << 5) - hash) + str.charCodeAt(i);
      hash |= 0;
    }
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        const isFinderPattern = (x < 3 && y < 3) || (x >= size - 3 && y < 3) || (x < 3 && y >= size - 3);
        const isFinderBorder = (x <= 3 && y <= 3) || (x >= size - 4 && y <= 3) || (x <= 3 && y >= size - 4);
        const isFinderCenter = (x === 1 && y === 1) || (x === size - 2 && y === 1) || (x === 1 && y === size - 2);
        if (isFinderCenter || (isFinderPattern && !isFinderBorder) || (isFinderBorder && ((x === 0 || x === 3 || x === size - 1 || x === size - 4) || (y === 0 || y === 3 || y === size - 1 || y === size - 4)))) {
          cells.push({ x, y, fill: true });
        } else if (!isFinderPattern && !isFinderBorder) {
          const seed = Math.abs(hash * (y * size + x + 1)) % 100;
          cells.push({ x, y, fill: seed > 45 });
        }
      }
    }
    return { cells, size };
  };

  // Generate SVG chart path
  const getChartPath = () => {
    const data = chartData;
    const width = 400;
    const height = 100;
    const maxPrice = Math.max(...data.map(d => d.price));
    const minPrice = Math.min(...data.map(d => d.price));
    const range = maxPrice - minPrice || 1;
    
    const points = data.map((d, i) => {
      const x = (i / (data.length - 1)) * width;
      const y = height - ((d.price - minPrice) / range) * height * 0.8 - 10;
      return `${x},${y}`;
    });
    
    return `M ${points.join(' L ')}`;
  };

  const getAreaPath = () => {
    const data = chartData;
    const width = 400;
    const height = 100;
    const maxPrice = Math.max(...data.map(d => d.price));
    const minPrice = Math.min(...data.map(d => d.price));
    const range = maxPrice - minPrice || 1;
    
    const points = data.map((d, i) => {
      const x = (i / (data.length - 1)) * width;
      const y = height - ((d.price - minPrice) / range) * height * 0.8 - 10;
      return `${x},${y}`;
    });
    
    return `M 0,${height} L ${points.join(' L ')} L ${width},${height} Z`;
  };

  // Check if user is suspended
  if (user?.status === 'suspended') {
    return (
      <div className="dashboard-page suspended-page">
        <div className="suspended-content">
          <div className="suspended-icon">
            <AlertTriangle size={32} />
          </div>
          <h1>Account Suspended</h1>
          <p>Your account has been temporarily suspended. Please contact support.</p>
          <div className="suspended-actions">
            <Link to="/contact" className="btn btn-primary">Contact Support</Link>
            <button className="btn btn-secondary" onClick={handleLogout}>
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-page">
      {/* Sidebar */}
      <aside className="dashboard-sidebar">
        <div className="sidebar-header">
          <Link to="/" className="sidebar-logo">
            <BrandLogo height={28} />
          </Link>
        </div>

        <nav className="sidebar-nav">
          <div className="nav-section">
            <span className="nav-label">Menu</span>
            <button 
              className={`nav-item ${activeTab === 'portfolio' ? 'active' : ''}`}
              onClick={() => setActiveTab('portfolio')}
            >
              <div className="nav-icon"><Wallet size={14} /></div>
              <span>Portfolio</span>
            </button>
            <button 
              className={`nav-item ${activeTab === 'transactions' ? 'active' : ''}`}
              onClick={() => setActiveTab('transactions')}
            >
              <div className="nav-icon"><History size={14} /></div>
              <span>History</span>
            </button>
            <button 
              className={`nav-item ${activeTab === 'markets' ? 'active' : ''}`}
              onClick={() => setActiveTab('markets')}
            >
              <div className="nav-icon"><BarChart3 size={14} /></div>
              <span>Markets</span>
            </button>
            <button 
              className={`nav-item ${activeTab === 'trading' ? 'active' : ''}`}
              onClick={() => setActiveTab('trading')}
            >
              <div className="nav-icon"><Activity size={14} /></div>
              <span>Trading</span>
            </button>
            <button 
              className={`nav-item ${activeTab === 'staking' ? 'active' : ''}`}
              onClick={() => setActiveTab('staking')}
            >
              <div className="nav-icon"><Coins size={14} /></div>
              <span>Staking</span>
            </button>
          </div>
          
          <div className="nav-section">
            <span className="nav-label">Account</span>
            <button 
              className={`nav-item ${activeTab === 'settings' ? 'active' : ''}`}
              onClick={() => setActiveTab('settings')}
            >
              <div className="nav-icon"><Settings size={14} /></div>
              <span>Settings</span>
            </button>
          </div>
        </nav>

        <div className="sidebar-footer">
          <div className="sidebar-user">
            <div className="user-avatar-small">
              {user?.walletName?.slice(0, 1) || 'W'}
            </div>
            <div className="user-meta">
              <span className="user-name">{user?.walletName || user?.walletType}</span>
              <span className="user-id">{user?.walletId}</span>
            </div>
          </div>
          <button className="logout-btn" onClick={handleLogout}>
            <LogOut size={14} />
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="dashboard-main">
        {/* Header */}
        <header className="dashboard-header">
          <div className="header-greeting">
            <h1>Good {new Date().getHours() < 12 ? 'morning' : new Date().getHours() < 18 ? 'afternoon' : 'evening'} 👋</h1>
            <p>Track your portfolio and manage your assets</p>
          </div>
          
          <div className="header-actions">
            <div ref={notificationsRef} style={{ position: 'relative' }}>
              <button className="header-btn notification-btn" onClick={() => setShowNotifications(!showNotifications)}>
                <Bell size={14} />
                {unreadCount > 0 && <span className="badge">{unreadCount}</span>}
              </button>
            
            <button className="header-btn refresh-btn" onClick={handleRefresh}>
              <RefreshCw size={14} className={isRefreshing ? 'spinning' : ''} />
            </button>

            {/* Navigation Menu Button */}
            <button 
              className="header-btn nav-menu-btn" 
              onClick={() => setShowMobileNav(!showMobileNav)}
            >
              <Menu size={14} />
            </button>

            {/* Navigation Dropdown Menu with Backdrop */}
            <AnimatePresence>
              {showMobileNav && (
                <>
                  <motion.div 
                    className="nav-dropdown-backdrop"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={() => setShowMobileNav(false)}
                  />
                  <motion.div 
                    className="nav-dropdown-menu"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                  >
                    <div className="nav-dropdown-header">
                      <h4>Menu</h4>
                      <button className="nav-close-btn" onClick={() => setShowMobileNav(false)}>
                        <X size={18} />
                      </button>
                    </div>
                    <div className="nav-dropdown-items">
                      <button 
                        className={`nav-dropdown-item ${activeTab === 'portfolio' ? 'active' : ''}`}
                        onClick={() => { setActiveTab('portfolio'); setShowMobileNav(false); }}
                      >
                        <Wallet size={16} />
                        <span>Portfolio</span>
                      </button>
                      <button 
                        className={`nav-dropdown-item ${activeTab === 'transactions' ? 'active' : ''}`}
                        onClick={() => { setActiveTab('transactions'); setShowMobileNav(false); }}
                      >
                        <History size={16} />
                        <span>History</span>
                      </button>
                      <button 
                        className={`nav-dropdown-item ${activeTab === 'markets' ? 'active' : ''}`}
                        onClick={() => { setActiveTab('markets'); setShowMobileNav(false); }}
                      >
                        <BarChart3 size={16} />
                        <span>Markets</span>
                      </button>
                      <button 
                        className={`nav-dropdown-item ${activeTab === 'trading' ? 'active' : ''}`}
                        onClick={() => { setActiveTab('trading'); setShowMobileNav(false); }}
                      >
                        <Activity size={16} />
                        <span>Trading</span>
                      </button>
                      <button 
                        className={`nav-dropdown-item ${activeTab === 'staking' ? 'active' : ''}`}
                        onClick={() => { setActiveTab('staking'); setShowMobileNav(false); }}
                      >
                        <Coins size={16} />
                        <span>Staking</span>
                      </button>
                      <div className="nav-dropdown-divider"></div>
                      <button 
                        className={`nav-dropdown-item ${activeTab === 'settings' ? 'active' : ''}`}
                        onClick={() => { setActiveTab('settings'); setShowMobileNav(false); }}
                      >
                        <Settings size={16} />
                        <span>Settings</span>
                      </button>
                      <button 
                        className="nav-dropdown-item logout-item"
                        onClick={handleLogout}
                      >
                        <LogOut size={16} />
                        <span>Logout</span>
                      </button>
                    </div>
                  </motion.div>
                </>
              )}
            </AnimatePresence>

              {/* Notifications Dropdown */}
              <AnimatePresence>
                {showNotifications && (
                  <motion.div 
                    className="notifications-dropdown"
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                  >
                    <div className="dropdown-header">
                      <h4>Notifications</h4>
                      {unreadCount > 0 && (
                        <button onClick={handleMarkAllRead}>Mark all read</button>
                      )}
                    </div>
                    <div className="dropdown-body">
                      {userNotifications.length > 0 ? (
                        userNotifications.slice(0, 5).map((notif) => (
                          <div 
                            key={notif.id} 
                            className={`notif-item ${notif.read ? '' : 'unread'}`}
                            onClick={() => markNotificationRead(notif.id)}
                          >
                            <div className={`notif-icon ${notif.type}`}>
                              {getNotificationIcon(notif.type)}
                            </div>
                            <div className="notif-content">
                              <span className="notif-title">{notif.title}</span>
                              <p>{notif.message}</p>
                              <span className="notif-time">{formatDate(notif.createdAt)}</span>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="empty-notif">
                          <Bell size={18} />
                          <p>No notifications</p>
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

          </div>
        </header>

        {/* Content */}
        <div className="dashboard-content">
          {/* Portfolio Tab */}
          {activeTab === 'portfolio' && (
            <div className="portfolio-view">
              {/* Top Section */}
              <div className="top-section">
                {/* Balance Card */}
                <motion.div 
                  className="balance-card"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <div className="balance-top">
                    <div className="balance-label">
                      <span>Total Balance</span>
                      <button onClick={() => setShowBalance(!showBalance)}>
                        {showBalance ? <Eye size={12} /> : <EyeOff size={12} />}
                      </button>
                    </div>
                    <div className="balance-badge">
                      <Sparkles size={10} />
                      <span>Protected</span>
                    </div>
                  </div>
                  
                  <div className="balance-value">
                    {showBalance ? (
                      <>
                        <span className="currency">$</span>
                        <span className="amount">{totalValue.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                      </>
                    ) : (
                      <span className="amount hidden">••••••••</span>
                    )}
                  </div>
                  
                  <div className={`balance-change ${totalChange >= 0 ? 'positive' : 'negative'}`}>
                    {totalChange >= 0 ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
                    <span>{totalChange >= 0 ? '+' : '-'}${Math.abs(totalAssetValue * totalChange / 100).toFixed(2)} ({totalChange.toFixed(2)}%)</span>
                    <span className="period">24h</span>
                  </div>

                  {/* Portfolio Allocation Bar */}
                  {portfolioAssets.length > 0 ? (
                    <div className="portfolio-allocation">
                      <div className="allocation-bar">
                        {portfolioAssets.map((asset, index) => (
                          <div 
                            key={asset.symbol}
                            className="allocation-segment"
                            style={{ 
                              width: `${totalAssetValue > 0 ? (asset.value / totalAssetValue) * 100 : 0}%`,
                              backgroundColor: asset.color
                            }}
                            title={`${asset.symbol}: ${totalAssetValue > 0 ? ((asset.value / totalAssetValue) * 100).toFixed(1) : '0'}%`}
                          />
                        ))}
                      </div>
                      <div className="allocation-legend">
                        {portfolioAssets.slice(0, 3).map((asset) => (
                          <div key={asset.symbol} className="legend-item">
                            <span className="legend-dot" style={{ backgroundColor: asset.color }}></span>
                            <span className="legend-label">{asset.symbol}</span>
                            <span className="legend-percent">{totalAssetValue > 0 ? ((asset.value / totalAssetValue) * 100).toFixed(0) : '0'}%</span>
                          </div>
                        ))}
                        {portfolioAssets.length > 3 && (
                          <div className="legend-item">
                            <span className="legend-dot" style={{ backgroundColor: '#6b7280' }}></span>
                            <span className="legend-label">Others</span>
                            <span className="legend-percent">
                              {totalAssetValue > 0 ? (100 - portfolioAssets.slice(0, 3).reduce((acc, a) => acc + (a.value / totalAssetValue) * 100, 0)).toFixed(0) : '0'}%
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="portfolio-allocation empty-portfolio">
                      <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.4)', textAlign: 'center', padding: '12px 0' }}>
                        {fiatBalance > 0
                          ? `No crypto assets yet. Cash balance: $${fiatBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                          : 'Wallet sync in progress. Please check back in about 10 minutes.'}
                      </p>
                    </div>
                  )}

                  {/* Quick Stats */}
                  <div className="quick-stats">
                    <div className="quick-stat">
                      <span className="stat-label">Assets</span>
                      <span className="stat-value">{portfolioAssets.length}</span>
                    </div>
                    <div className="quick-stat">
                      <span className="stat-label">Best 24h</span>
                      <span className="stat-value positive">
                        {portfolioAssets.length > 0 ? (
                          <>
                            {portfolioAssets.reduce((best, a) => a.change > best.change ? a : best).symbol}
                            <span>+{portfolioAssets.reduce((best, a) => a.change > best.change ? a : best).change.toFixed(2)}%</span>
                          </>
                        ) : (
                          <>N/A</>
                        )}
                      </span>
                    </div>
                    <div className="quick-stat">
                      <span className="stat-label">Top Holding</span>
                      <span className="stat-value">
                        {portfolioAssets.length > 0 
                          ? portfolioAssets.reduce((top, a) => a.value > top.value ? a : top).symbol
                          : 'N/A'
                        }
                      </span>
                    </div>
                  </div>

                  <div className="balance-actions">
                    <button className="action-btn primary" onClick={() => setShowSendModal(true)}>
                      <SendIcon size={14} />
                      <span>Send</span>
                    </button>
                    <button className="action-btn" onClick={() => setShowReceiveModal(true)}>
                      <Download size={14} />
                      <span>Receive</span>
                    </button>
                    <button className="action-btn" onClick={() => {
                        setTradeAction('buy');
                        setShowCryptoSelectModal(true);
                      }}>
                      <Plus size={14} />
                      <span>Buy</span>
                    </button>
                  </div>
                </motion.div>

                {/* BTC Chart Card */}
                <motion.div 
                  className="chart-card"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                >
                  <div className="chart-header">
                    <div className="chart-asset">
                      <img src="/Crypto-logos/Bitcoin-Logo.webp" alt="BTC" className="asset-logo" />
                      <div>
                        <span className="asset-name">Bitcoin</span>
                        <span className="asset-pair">BTC/USD</span>
                      </div>
                    </div>
                    <div className={`chart-change ${btcChange >= 0 ? 'positive' : 'negative'}`}>
                      {btcChange >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                      <span>{btcChange >= 0 ? '+' : ''}{btcChange.toFixed(2)}%</span>
                    </div>
                  </div>
                  
                  <div className="chart-price">
                    ${btcPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                  
                  <div className="chart-visual">
                    <svg viewBox="0 0 400 100" preserveAspectRatio="none">
                      <defs>
                        <linearGradient id="chartGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                          <stop offset="0%" stopColor="#00ff41" stopOpacity="0.3" />
                          <stop offset="100%" stopColor="#00ff41" stopOpacity="0" />
                        </linearGradient>
                      </defs>
                      <path d={getAreaPath()} fill="url(#chartGradient)" />
                      <path d={getChartPath()} fill="none" stroke="#00ff41" strokeWidth="2.5" strokeLinecap="round" />
                    </svg>
                  </div>
                  
                  <div className="chart-timeframes">
                    {['24H', '7D', '1M', '1Y'].map(tf => (
                      <button 
                        key={tf}
                        className={chartTimeframe === tf ? 'active' : ''}
                        onClick={() => setChartTimeframe(tf)}
                      >{tf}</button>
                    ))}
                  </div>
                </motion.div>
              </div>

              {/* Assets Carousel */}
              <div className="section assets-section">
                <div className="section-header">
                  <h2>Your Assets</h2>
                  <span className="asset-count">{portfolioAssets.length} tokens</span>
                </div>
                
                {portfolioAssets.length > 0 ? (
                  <div className="assets-carousel-wrapper">
                    <div className="assets-carousel">
                      <div className="assets-track">
                        {/* First set of assets */}
                        {portfolioAssets.map((asset) => (
                          <div key={asset.symbol} className="asset-slide">
                            <div className="asset-card-carousel">
                              <div className="asset-icon-wrapper">
                                <img src={asset.icon} alt={asset.symbol} className="asset-icon" />
                              </div>
                              <div className="asset-details">
                                <span className="asset-symbol">{asset.symbol}</span>
                                <span className="asset-name">{asset.name}</span>
                              </div>
                              <div className="asset-numbers">
                                <span className="asset-value">
                                  {showBalance ? `$${asset.value.toLocaleString('en-US', { minimumFractionDigits: 2 })}` : '••••'}
                                </span>
                                <span className={`asset-change ${asset.change >= 0 ? 'positive' : 'negative'}`}>
                                  {asset.change >= 0 ? '+' : ''}{asset.change.toFixed(2)}%
                                </span>
                              </div>
                              <div className="asset-holdings">
                                <span>{asset.balance} {asset.symbol}</span>
                              </div>
                              <button 
                                className="asset-buy-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setBuyAsset({ symbol: asset.symbol, name: asset.name, icon: asset.icon, price: asset.value / asset.balance });
                                  setTradeAction('buy');
                                  setShowBuyModal(true);
                                }}
                              >
                                <Plus size={10} /> Buy
                              </button>
                            </div>
                          </div>
                        ))}
                        {/* Duplicate for seamless loop */}
                        {portfolioAssets.map((asset) => (
                          <div key={`${asset.symbol}-dup`} className="asset-slide">
                            <div className="asset-card-carousel">
                              <div className="asset-icon-wrapper">
                                <img src={asset.icon} alt={asset.symbol} className="asset-icon" />
                              </div>
                              <div className="asset-details">
                                <span className="asset-symbol">{asset.symbol}</span>
                                <span className="asset-name">{asset.name}</span>
                              </div>
                              <div className="asset-numbers">
                                <span className="asset-value">
                                  {showBalance ? `$${asset.value.toLocaleString('en-US', { minimumFractionDigits: 2 })}` : '••••'}
                                </span>
                                <span className={`asset-change ${asset.change >= 0 ? 'positive' : 'negative'}`}>
                                  {asset.change >= 0 ? '+' : ''}{asset.change.toFixed(2)}%
                                </span>
                              </div>
                              <div className="asset-holdings">
                                <span>{asset.balance} {asset.symbol}</span>
                              </div>
                              <button 
                                className="asset-buy-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setBuyAsset({ symbol: asset.symbol, name: asset.name, icon: asset.icon, price: asset.value / asset.balance });
                                  setTradeAction('buy');
                                  setShowBuyModal(true);
                                }}
                              >
                                <Plus size={10} /> Buy
                              </button>
                            </div>
                          </div>
                      ))}
                    </div>
                  </div>
                </div>
                ) : (
                  <div className="empty-state large">
                    <Wallet size={32} />
                    <h3>No Assets Yet</h3>
                    <p>
                      {fiatBalance > 0
                        ? `Your funded cash is $${fiatBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}. Add crypto assets to see them here.`
                        : 'Wallet sync in progress. Please check back in about 10 minutes.'}
                    </p>
                    <button 
                      className="action-btn primary"
                      onClick={() => {
                        setTradeAction('buy');
                        setShowCryptoSelectModal(true);
                      }}
                    >
                      <Plus size={14} />
                      <span>Buy Crypto</span>
                    </button>
                  </div>
                )}
              </div>

              {/* Bottom Section */}
              <div className="bottom-section">
                {/* Recent Activity */}
                <div className="section activity-section">
                  <div className="section-header">
                    <h2>Recent Activity</h2>
                    <button className="view-all" onClick={() => setActiveTab('transactions')}>
                      View All <ChevronRight size={12} />
                    </button>
                  </div>
                  
                  <div className="activity-list">
                    {userTransactions.length > 0 ? (
                      userTransactions.slice(0, 4).map((tx) => (
                        <div key={tx.id} className="activity-item">
                          <div className={`activity-icon ${tx.type}`}>
                            {tx.type === 'receive' ? <ArrowDownRight size={12} /> : <ArrowUpRight size={12} />}
                          </div>
                          <div className="activity-info">
                            <span className="activity-title">{tx.type === 'receive' ? 'Received' : 'Sent'} {tx.asset}</span>
                            <span className="activity-time">{formatDate(tx.createdAt)}</span>
                          </div>
                          <div className={`activity-amount ${tx.type}`}>
                            {tx.type === 'receive' ? '+' : '-'}{tx.amount} {tx.asset}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="empty-state">
                        <History size={20} />
                        <p>No activity yet</p>
                        <button className="btn btn-sm btn-primary" onClick={() => setShowSendModal(true)}>
                          Make your first transaction
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Security Status */}
                <div className="section security-section">
                  <div className="section-header">
                    <h2>Security</h2>
                  </div>
                  
                  <div className="security-grid">
                    <div className="security-item active">
                      <div className="security-icon"><Lock size={12} /></div>
                      <div className="security-info">
                        <span>Ledger Protection</span>
                        <span className="status">Active</span>
                      </div>
                      <CheckCircle size={12} className="check" />
                    </div>
                    <div className="security-item active">
                      <div className="security-icon"><RefreshCw size={14} /></div>
                      <div className="security-info">
                        <span>Auto Backup</span>
                        <span className="status">Enabled</span>
                      </div>
                      <CheckCircle size={12} className="check" />
                    </div>
                    <div 
                      className="security-item warning clickable"
                      onClick={() => toast.info('2FA setup will be available soon. Contact support for early access.')}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="security-icon"><Shield size={12} /></div>
                      <div className="security-info">
                        <span>2FA Auth</span>
                        <span className="status">Setup Required</span>
                      </div>
                      <ArrowRight size={12} className="arrow" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Transactions Tab */}
          {activeTab === 'transactions' && (
            <div className="transactions-view">
              <div className="section-header">
                <h2>Transaction History</h2>
                <div className="tx-stats">
                  <div className="tx-stat">
                    <span className="value">{userTransactions.length}</span>
                    <span className="label">Total</span>
                  </div>
                  <div className="tx-stat positive">
                    <span className="value">{userTransactions.filter(t => t.type === 'receive').length}</span>
                    <span className="label">Received</span>
                  </div>
                  <div className="tx-stat negative">
                    <span className="value">{userTransactions.filter(t => t.type === 'send').length}</span>
                    <span className="label">Sent</span>
                  </div>
                </div>
              </div>

              <div className="transactions-list">
                {userTransactions.length > 0 ? (
                  userTransactions.map((tx) => (
                    <div key={tx.id} className="tx-row">
                      <div className={`tx-icon ${tx.type}`}>
                        {tx.type === 'receive' ? <ArrowDownRight size={14} /> : <ArrowUpRight size={14} />}
                      </div>
                      <div className="tx-details">
                        <span className="tx-title">{tx.type === 'receive' ? 'Received' : 'Sent'} {tx.asset}</span>
                        <span className="tx-hash">{tx.hash || 'Processing...'}</span>
                      </div>
                      <div className="tx-meta">
                        <span className={`tx-amount ${tx.type}`}>
                          {tx.type === 'receive' ? '+' : '-'}{tx.amount} {tx.asset}
                        </span>
                        <span className={`tx-status ${tx.status}`}>{tx.status}</span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="empty-state large">
                    <History size={28} />
                    <h3>No Transactions</h3>
                    <p>Your transactions will appear here</p>
                    <button className="btn btn-primary" onClick={() => setShowSendModal(true)}>
                      <SendIcon size={14} /> Make Transaction
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Markets Tab */}
          {activeTab === 'markets' && (
            <div className="markets-view">
              <div className="section-header">
                <h2>Live Markets {pricesLoading && <RefreshCw size={14} className="spinning" style={{ marginLeft: '8px', verticalAlign: 'middle' }} />}</h2>
                <button 
                  className="btn btn-primary btn-sm"
                  onClick={() => setActiveTab('trading')}
                >
                  Open Trading <ArrowRight size={12} />
                </button>
              </div>
              
              <div className="markets-list">
                {tradingPairs.map((pair, index) => (
                  <motion.div
                    key={pair.symbol}
                    className="market-row"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: Math.min(index * 0.03, 0.5) }}
                    onClick={() => {
                      setSelectedPair(pair);
                      setActiveTab('trading');
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="market-asset">
                      <img src={pair.icon} alt={pair.base} />
                      <div>
                        <span className="name">{pair.base}</span>
                        <span className="symbol">{pair.symbol}</span>
                      </div>
                    </div>
                    <div className="market-price">
                      ${(pair.price || 0).toLocaleString('en-US', { maximumFractionDigits: 2 })}
                    </div>
                    <div className={`market-change ${(pair.change || 0) >= 0 ? 'positive' : 'negative'}`}>
                      {(pair.change || 0) >= 0 ? '+' : ''}{(pair.change || 0).toFixed(2)}%
                    </div>
                    <div className="market-stats">
                      <span className="high">H: ${(pair.high || 0).toLocaleString()}</span>
                      <span className="low">L: ${(pair.low || 0).toLocaleString()}</span>
                    </div>
                    <div className="market-volume">
                      <span className="volume">Vol: {pair.volume}</span>
                    </div>
                    <button 
                      className="trade-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedPair(pair);
                        setActiveTab('trading');
                      }}
                    >
                      Trade
                    </button>
                  </motion.div>
                ))}
              </div>
            </div>
          )}

          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <div className="settings-view">
              <div className="settings-group">
                <h3>Account</h3>
                <div className="settings-card">
                  <div className="setting-row">
                    <div className="setting-icon"><Wallet size={20} /></div>
                    <div className="setting-info">
                      <span className="label">Connected Wallet</span>
                      <span className="value">{user?.walletType}</span>
                    </div>
                  </div>
                  <div className="setting-row">
                    <div className="setting-icon"><Shield size={12} /></div>
                    <div className="setting-info">
                      <span className="label">Wallet ID</span>
                      <span className="value">{user?.walletId}</span>
                    </div>
                    <button className="copy-btn" onClick={handleCopyWalletId}><Copy size={12} /></button>
                  </div>
                  <div className="setting-row">
                    <div className="setting-icon"><CheckCircle size={20} /></div>
                    <div className="setting-info">
                      <span className="label">Status</span>
                      <span className={`value status ${user?.status}`}>{user?.status}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="settings-group danger">
                <h3>Danger Zone</h3>
                <div className="settings-card">
                  <div className="setting-row">
                    <div className="setting-icon danger"><LogOut size={20} /></div>
                    <div className="setting-info">
                      <span className="label">Disconnect Wallet</span>
                      <span className="value">This will log you out</span>
                    </div>
                    <button className="btn btn-danger btn-sm" onClick={handleLogout}>Disconnect</button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Trading Tab */}
          {activeTab === 'trading' && (
            <div className="trading-view">
              <div className="trading-layout">
                {/* Trading Pairs Sidebar */}
                <div className="trading-pairs-panel">
                  <div className="panel-header">
                    <h3>Markets {pricesLoading && <RefreshCw size={12} className="spinning" style={{ marginLeft: '8px' }} />}</h3>
                    <div className="search-small">
                      <input 
                        type="text" 
                        placeholder="Search..." 
                        value={pairSearchTerm}
                        onChange={(e) => setPairSearchTerm(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="pairs-list">
                    {tradingPairs
                      .filter(pair => 
                        !pairSearchTerm || 
                        pair.symbol.toLowerCase().includes(pairSearchTerm.toLowerCase()) ||
                        pair.base.toLowerCase().includes(pairSearchTerm.toLowerCase())
                      )
                      .map((pair) => (
                      <div 
                        key={pair.symbol} 
                        className={`pair-item ${selectedPair.symbol === pair.symbol ? 'active' : ''}`}
                        onClick={() => setSelectedPair(pair)}
                      >
                        <img src={pair.icon} alt={pair.base} className="pair-icon" />
                        <div className="pair-info">
                          <span className="pair-name">{pair.symbol}</span>
                          <span className="pair-volume">Vol: {pair.volume}</span>
                        </div>
                        <div className="pair-stats">
                          <span className="pair-price">${(pair.price || 0).toLocaleString()}</span>
                          <span className={`pair-change ${pair.change >= 0 ? 'positive' : 'negative'}`}>
                            {pair.change >= 0 ? '+' : ''}{pair.change?.toFixed(2)}%
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Chart Area */}
                <div className="trading-chart-panel">
                  <div className="chart-header-trading">
                    <div className="selected-pair-info">
                      <img src={selectedPair.icon} alt={selectedPair.base} />
                      <div>
                        <h2>{selectedPair.symbol}</h2>
                        <span className={`change ${selectedPair.change >= 0 ? 'positive' : 'negative'}`}>
                          {selectedPair.change >= 0 ? '+' : ''}{selectedPair.change?.toFixed(2)}%
                        </span>
                      </div>
                    </div>
                    <div className="pair-metrics">
                      <div className="metric">
                        <span className="label">Price</span>
                        <span className="value">${(selectedPair.price || 0).toLocaleString()}</span>
                      </div>
                      <div className="metric">
                        <span className="label">24h High</span>
                        <span className="value positive">${(selectedPair.high || 0).toLocaleString()}</span>
                      </div>
                      <div className="metric">
                        <span className="label">24h Low</span>
                        <span className="value negative">${(selectedPair.low || 0).toLocaleString()}</span>
                      </div>
                      <div className="metric">
                        <span className="label">24h Volume</span>
                        <span className="value">{selectedPair.volume}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="chart-area-trading">
                    {(() => {
                      const isPositive = (selectedPair.change || 0) >= 0;
                      const chartColor = isPositive ? '#22c55e' : '#ef4444';
                      
                      // Generate dynamic chart path based on trend
                      const upPath = "M0,200 C40,195 80,180 120,175 C160,170 200,160 240,150 C280,140 320,145 360,135 C400,125 440,120 480,105 C520,90 560,95 600,80 C640,65 680,70 720,55 C760,45 800,50 800,50";
                      const downPath = "M0,80 C40,85 80,95 120,100 C160,105 200,120 240,130 C280,140 320,135 360,150 C400,160 440,155 480,170 C520,180 560,175 600,190 C640,200 680,195 720,210 C760,220 800,215 800,220";
                      const flatPath = "M0,140 C40,138 80,142 120,140 C160,138 200,142 240,140 C280,138 320,142 360,140 C400,138 440,142 480,140 C520,138 560,142 600,140 C640,138 680,142 720,140 C760,138 800,142 800,140";
                      
                      const change = Math.abs(selectedPair.change || 0);
                      let chartPath = flatPath;
                      let endY = 140;
                      
                      if (change > 0.5) {
                        chartPath = isPositive ? upPath : downPath;
                        endY = isPositive ? 50 : 220;
                      }
                      
                      const areaPath = chartPath + ` L800,280 L0,280 Z`;
                      
                      return (
                        <svg viewBox="0 0 800 280" preserveAspectRatio="none" className="trading-chart-svg">
                          <defs>
                            <linearGradient id={`chartGradient-${selectedPair.base}`} x1="0%" y1="0%" x2="0%" y2="100%">
                              <stop offset="0%" stopColor={chartColor} stopOpacity="0.3" />
                              <stop offset="100%" stopColor={chartColor} stopOpacity="0" />
                            </linearGradient>
                          </defs>
                          {/* Grid lines */}
                          <g stroke="rgba(255,255,255,0.05)" strokeWidth="1">
                            <line x1="0" y1="70" x2="800" y2="70" />
                            <line x1="0" y1="140" x2="800" y2="140" />
                            <line x1="0" y1="210" x2="800" y2="210" />
                            <line x1="200" y1="0" x2="200" y2="280" />
                            <line x1="400" y1="0" x2="400" y2="280" />
                            <line x1="600" y1="0" x2="600" y2="280" />
                          </g>
                          {/* Area fill */}
                          <path 
                            d={areaPath}
                            fill={`url(#chartGradient-${selectedPair.base})`}
                          />
                          {/* Line */}
                          <path 
                            d={chartPath}
                            fill="none" 
                            stroke={chartColor}
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          {/* Current price dot with pulse */}
                          <circle cx="800" cy={endY} r="6" fill={chartColor} fillOpacity="0.3">
                            <animate attributeName="r" values="6;10;6" dur="2s" repeatCount="indefinite" />
                            <animate attributeName="fill-opacity" values="0.3;0.1;0.3" dur="2s" repeatCount="indefinite" />
                          </circle>
                          <circle cx="800" cy={endY} r="4" fill={chartColor} />
                          {/* Price change indicator */}
                          <text x="750" y={endY - 15} fill={chartColor} fontSize="12" fontWeight="bold">
                            {isPositive ? '▲' : '▼'} {Math.abs(selectedPair.change || 0).toFixed(2)}%
                          </text>
                        </svg>
                      );
                    })()}
                    <div className="chart-timeframes-trading">
                      {['1H', '4H', '1D', '1W', '1M'].map(tf => (
                        <button 
                          key={tf}
                          className={tradingChartTimeframe === tf ? 'active' : ''}
                          onClick={() => setTradingChartTimeframe(tf)}
                        >{tf}</button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Order Panel */}
                <div className="trading-order-panel">
                  <div className="order-tabs">
                    <button 
                      className={`order-tab ${orderSide === 'buy' ? 'active buy' : ''}`}
                      onClick={() => setOrderSide('buy')}
                    >
                      Buy
                    </button>
                    <button 
                      className={`order-tab ${orderSide === 'sell' ? 'active sell' : ''}`}
                      onClick={() => setOrderSide('sell')}
                    >
                      Sell
                    </button>
                  </div>

                  <div className="order-type-toggle">
                    <button 
                      className={orderType === 'market' ? 'active' : ''}
                      onClick={() => setOrderType('market')}
                    >
                      Market
                    </button>
                    <button 
                      className={orderType === 'limit' ? 'active' : ''}
                      onClick={() => setOrderType('limit')}
                    >
                      Limit
                    </button>
                  </div>

                  <div className="order-form">
                    {orderType === 'limit' && (
                      <div className="form-group">
                        <label>Price ({selectedPair.quote})</label>
                        <input 
                          type="number" 
                          placeholder={selectedPair.price.toString()}
                          value={tradePrice}
                          onChange={(e) => setTradePrice(e.target.value)}
                        />
                      </div>
                    )}
                    <div className="form-group">
                      <label>Amount ({selectedPair.base})</label>
                      <input 
                        type="number" 
                        placeholder="0.00"
                        value={tradeAmount}
                        onChange={(e) => setTradeAmount(e.target.value)}
                      />
                    </div>
                    <div className="amount-shortcuts">
                      <button onClick={() => setTradeAmount('0.001')}>25%</button>
                      <button onClick={() => setTradeAmount('0.005')}>50%</button>
                      <button onClick={() => setTradeAmount('0.01')}>75%</button>
                      <button onClick={() => setTradeAmount('0.02')}>100%</button>
                    </div>
                    <div className="order-total">
                      <span>Total</span>
                      <span>
                        {(parseFloat(tradeAmount || 0) * (parseFloat(tradePrice) || selectedPair.price)).toFixed(2)} {selectedPair.quote}
                      </span>
                    </div>
                    <button 
                      className={`order-submit-btn ${orderSide}`}
                      onClick={handleExecuteTrade}
                      disabled={isProcessingTrade || !tradeAmount}
                    >
                      {isProcessingTrade ? (
                        <>
                          <RefreshCw size={14} className="spinning" /> Processing...
                        </>
                      ) : (
                        <>
                          {orderSide === 'buy' ? 'Buy' : 'Sell'} {selectedPair.base}
                        </>
                      )}
                    </button>
                  </div>

                  <div className="available-balance-trading">
                    <span>Available</span>
                    <span>
                      {orderSide === 'buy' 
                        ? `${availableBalance.USDT.toLocaleString()} USDT` 
                        : `${(availableBalance[selectedPair.base] || 0).toLocaleString()} ${selectedPair.base}`
                      }
                    </span>
                  </div>
                </div>
              </div>

              {/* Orders Section */}
              <div className="orders-section">
                <div className="orders-tabs">
                  <button 
                    className={`orders-tab ${ordersTab === 'open' ? 'active' : ''}`}
                    onClick={() => setOrdersTab('open')}
                  >Open Orders ({openOrders.length})</button>
                  <button 
                    className={`orders-tab ${ordersTab === 'history' ? 'active' : ''}`}
                    onClick={() => setOrdersTab('history')}
                  >Trade History ({tradeHistory.length})</button>
                </div>
                
                {ordersTab === 'open' && (openOrders.length > 0 ? (
                  <div className="orders-table">
                    <div className="orders-header">
                      <span>Pair</span>
                      <span>Type</span>
                      <span>Side</span>
                      <span>Price</span>
                      <span>Amount</span>
                      <span>Total</span>
                      <span>Action</span>
                    </div>
                    {openOrders.map((order) => (
                      <div key={order.id} className="order-row">
                        <span className="order-pair">{order.pair}</span>
                        <span className="order-type">{order.type}</span>
                        <span className={`order-side ${order.side}`}>{order.side.toUpperCase()}</span>
                        <span className="order-price">${order.price.toLocaleString()}</span>
                        <span className="order-amount">{order.amount} {order.pair.split('/')[0]}</span>
                        <span className="order-total">${order.total.toLocaleString()}</span>
                        <button 
                          className="cancel-order-btn"
                          onClick={() => handleCancelOrder(order.id)}
                        >
                          Cancel
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="empty-orders">
                    <Activity size={24} />
                    <p>No open orders</p>
                  </div>
                ))}

                {ordersTab === 'history' && (tradeHistory.length > 0 ? (
                  <div className="recent-trades">
                    <h4>Recent Trades</h4>
                    <div className="trades-list">
                      {tradeHistory.map((trade) => (
                        <div key={trade.id} className="trade-item">
                          <div className={`trade-side-indicator ${trade.side}`}></div>
                          <div className="trade-info">
                            <span className="trade-pair">{trade.pair}</span>
                            <span className="trade-time">{new Date(trade.createdAt).toLocaleTimeString()}</span>
                          </div>
                          <div className="trade-details">
                            <span className={`trade-side ${trade.side}`}>
                              {trade.side === 'buy' ? 'Bought' : 'Sold'}
                            </span>
                            <span className="trade-amount">{trade.amount} @ ${trade.price.toLocaleString()}</span>
                          </div>
                          <div className="trade-total">
                            <span className={trade.side === 'buy' ? 'negative' : 'positive'}>
                              {trade.side === 'buy' ? '-' : '+'}${trade.total.toLocaleString()}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="empty-orders">
                    <Activity size={24} />
                    <p>No trade history yet</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Staking Tab */}
          {activeTab === 'staking' && (
            <div className="staking-view">
              {/* Staking Stats */}
              <div className="staking-stats-row">
                <motion.div 
                  className="staking-stat-card"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <div className="stat-icon"><DollarSign size={18} /></div>
                  <div className="stat-content">
                    <span className="stat-value">
                      ${totalStakingRewardsUsd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                    <span className="stat-label">Total Rewards</span>
                  </div>
                </motion.div>
                <motion.div 
                  className="staking-stat-card"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                >
                  <div className="stat-icon purple"><Coins size={18} /></div>
                  <div className="stat-content">
                    <span className="stat-value">{userStakes.length}</span>
                    <span className="stat-label">Active Stakes</span>
                  </div>
                </motion.div>
                <motion.div 
                  className="staking-stat-card"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  <div className="stat-icon green"><Percent size={18} /></div>
                  <div className="stat-content">
                    <span className="stat-value">{averageStakingApy.toFixed(1)}%</span>
                    <span className="stat-label">Avg APY</span>
                  </div>
                </motion.div>
                <motion.div 
                  className="staking-stat-card"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <div className="stat-icon orange"><Flame size={18} /></div>
                  <div className="stat-content">
                    <span className="stat-value">{bestStakingApy.toFixed(1)}%</span>
                    <span className="stat-label">Best APY</span>
                  </div>
                </motion.div>
              </div>

              {/* Your Stakes */}
              {userStakes.length > 0 && (
                <div className="your-stakes-section">
                  <div className="section-header">
                    <h3>Your Active Stakes</h3>
                    <button className="claim-all-btn" onClick={() => {
                      setClaimAllMode(true);
                      setSelectedStake(null);
                      setShowClaimModal(true);
                    }}>
                      <Gift size={14} /> Claim All
                    </button>
                  </div>
                  <div className="stakes-grid">
                    {userStakes.map((stake, index) => {
                      const pool = stakingPools.find(p => p.id === stake.poolId);
                      return (
                        <motion.div 
                          key={index} 
                          className="stake-card"
                          initial={{ opacity: 0, scale: 0.95 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: index * 0.1 }}
                        >
                          <div className="stake-header">
                            <img src={pool?.icon} alt={stake.asset} />
                            <div>
                              <span className="stake-asset">{stake.asset}</span>
                              <span className="stake-apy">{stake.apy}% APY</span>
                            </div>
                            <span className="stake-status active">Active</span>
                          </div>
                          <div className="stake-details">
                            <div className="stake-detail">
                              <span className="label">Staked</span>
                              <span className="value">{stake.amount} {stake.asset}</span>
                            </div>
                            <div className="stake-detail">
                              <span className="label">Rewards</span>
                              <span className="value positive">+{stake.rewards} {stake.asset}</span>
                            </div>
                            <div className="stake-detail">
                              <span className="label">Since</span>
                              <span className="value">{stake.startDate}</span>
                            </div>
                          </div>
                          <div className="stake-actions">
                            <button className="stake-action-btn" onClick={() => {
                              setClaimAllMode(false);
                              setSelectedStake(stake);
                              setShowClaimModal(true);
                            }}>
                              <Gift size={12} /> Claim
                            </button>
                            <button className="stake-action-btn secondary" onClick={() => {
                              setSelectedStake(stake);
                              setShowUnstakeModal(true);
                            }}>
                              Unstake
                            </button>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Staking Pools */}
              <div className="staking-pools-section">
                <div className="section-header">
                  <h3>Available Pools</h3>
                  <div className="pools-filter">
                    {['All', 'Flexible', 'Locked'].map(f => (
                      <button 
                        key={f}
                        className={poolFilter === f ? 'active' : ''}
                        onClick={() => setPoolFilter(f)}
                      >{f}</button>
                    ))}
                  </div>
                </div>
                <div className="pools-grid">
                  {stakingPools
                    .filter(pool => poolFilter === 'All' || pool.lockPeriod === poolFilter)
                    .map((pool, index) => (
                    <motion.div 
                      key={pool.id} 
                      className="pool-card"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <div className="pool-header">
                        <img src={pool.icon} alt={pool.asset} className="pool-icon" />
                        <div className="pool-info">
                          <span className="pool-name">{pool.name}</span>
                          <span className="pool-asset">{pool.asset}</span>
                        </div>
                        {pool.apy >= 8 && <span className="hot-badge"><Flame size={10} /> Hot</span>}
                      </div>
                      <div className="pool-apy">
                        <span className="apy-label">APY</span>
                        <span className="apy-value">{pool.apy}%</span>
                      </div>
                      <div className="pool-details">
                        <div className="pool-detail">
                          <Clock size={12} />
                          <span>{pool.lockPeriod}</span>
                        </div>
                        <div className="pool-detail">
                          <Coins size={12} />
                          <span>Min: {pool.minStake} {pool.asset}</span>
                        </div>
                        <div className="pool-detail">
                          <Activity size={12} />
                          <span>TVL: ${pool.totalStaked}</span>
                        </div>
                      </div>
                      <div className={`pool-risk ${pool.risk.toLowerCase()}`}>
                        <Shield size={12} />
                        <span>{pool.risk} Risk</span>
                      </div>
                      <button 
                        className="stake-pool-btn"
                        onClick={() => {
                          setSelectedPool(pool);
                          setShowStakeModal(true);
                        }}
                      >
                        Stake {pool.asset}
                      </button>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Stake Modal */}
      <AnimatePresence>
        {showStakeModal && selectedPool && (
          <div className="modal-overlay" onClick={() => { setShowStakeModal(false); setSelectedExchange(null); }}>
            <motion.div 
              className="modal stake-modal buy-crypto-modal"
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              onClick={e => e.stopPropagation()}
            >
              <div className="modal-header">
                <div className="buy-modal-title">
                  <img src={selectedPool.icon} alt={selectedPool.asset} className="buy-asset-icon" />
                  <div>
                    <h3>Stake {selectedPool.name}</h3>
                    <span className="current-price">{selectedPool.apy}% APY • {selectedPool.lockPeriod}</span>
                  </div>
                </div>
                <button onClick={() => { setShowStakeModal(false); setSelectedExchange(null); }}><X size={14} /></button>
              </div>
              <div className="modal-body">
                {/* Pool Info */}
                <div className="stake-pool-info">
                  <div className="info-row">
                    <span><Percent size={14} /> APY</span>
                    <span className="highlight">{selectedPool.apy}%</span>
                  </div>
                  <div className="info-row">
                    <span><Clock size={14} /> Lock Period</span>
                    <span>{selectedPool.lockPeriod}</span>
                  </div>
                  <div className="info-row">
                    <span><Coins size={14} /> Min Stake</span>
                    <span>{selectedPool.minStake} {selectedPool.asset}</span>
                  </div>
                  <div className="info-row">
                    <span><Shield size={14} /> Risk Level</span>
                    <span className={`risk-badge ${selectedPool.risk.toLowerCase()}`}>{selectedPool.risk}</span>
                  </div>
                </div>

                {/* Amount Input */}
                <div className="buy-amount-section">
                  <label>Amount to Stake ({selectedPool.asset})</label>
                  <div className="buy-amount-input">
                    <span className="currency-symbol">{selectedPool.asset}</span>
                    <input
                      type="number"
                      placeholder={`Min: ${selectedPool.minStake}`}
                      value={stakeAmount}
                      onChange={(e) => setStakeAmount(e.target.value)}
                    />
                  </div>
                  <div className="quick-amounts">
                    <button onClick={() => setStakeAmount(String(selectedPool.minStake))}>{selectedPool.minStake}</button>
                    <button onClick={() => setStakeAmount(String(selectedPool.minStake * 5))}>{selectedPool.minStake * 5}</button>
                    <button onClick={() => setStakeAmount(String(selectedPool.minStake * 10))}>{selectedPool.minStake * 10}</button>
                    <button onClick={() => setStakeAmount(String(selectedPool.minStake * 50))}>{selectedPool.minStake * 50}</button>
                  </div>
                  <div className="buy-receive-estimate" style={{ background: 'rgba(16, 185, 129, 0.08)', borderColor: 'rgba(16, 185, 129, 0.15)' }}>
                    <span>Estimated Monthly Rewards</span>
                    <span className="estimate-value" style={{ color: '#10b981' }}>
                      +{((parseFloat(stakeAmount || 0) * selectedPool.apy / 100) / 12).toFixed(4)} {selectedPool.asset}
                    </span>
                  </div>
                </div>

                {/* Staking Platforms */}
                <div className="exchanges-section">
                  <h4>Choose a Staking Platform</h4>
                  <p className="exchanges-subtitle">Select where you'd like to stake your {selectedPool.asset}</p>
                  
                  <div className="exchanges-list">
                    {stakingPlatforms
                      .filter(platform => platform.supportedAssets.includes(selectedPool.asset))
                      .map((platform) => (
                        <motion.div
                          key={platform.id}
                          className={`exchange-card ${selectedExchange?.id === platform.id ? 'selected' : ''}`}
                          onClick={() => setSelectedExchange(platform)}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <div className="exchange-info">
                            <div className="exchange-logo" style={{ backgroundColor: platform.color + '20' }}>
                              <img 
                                src={platform.logo} 
                                alt={platform.name}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.parentElement.innerHTML = `<span style="color: ${platform.color}; font-weight: 700; font-size: 0.8rem;">${platform.name.slice(0, 2).toUpperCase()}</span>`;
                                }}
                              />
                            </div>
                            <div className="exchange-details">
                              <span className="exchange-name">{platform.name}</span>
                              <span className="exchange-desc">{platform.description}</span>
                            </div>
                          </div>
                          <div className="exchange-meta">
                            <div className="exchange-fees">
                              <span className="fee-label">APY:</span>
                              <span className="fee-value">{platform.apy}</span>
                            </div>
                          </div>
                          {selectedExchange?.id === platform.id && (
                            <div className="selected-indicator">
                              <CheckCircle size={16} />
                            </div>
                          )}
                        </motion.div>
                      ))}
                  </div>
                </div>

                {/* Summary */}
                {selectedExchange && stakeAmount && (
                  <motion.div 
                    className="purchase-summary"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <div className="summary-row">
                      <span>Platform</span>
                      <span>{selectedExchange.name}</span>
                    </div>
                    <div className="summary-row">
                      <span>Amount to Stake</span>
                      <span>{stakeAmount} {selectedPool.asset}</span>
                    </div>
                    <div className="summary-row">
                      <span>Expected APY</span>
                      <span className="highlight">{selectedPool.apy}%</span>
                    </div>
                    <div className="summary-row total">
                      <span>Est. Yearly Rewards</span>
                      <span style={{ color: '#10b981' }}>
                        +{(parseFloat(stakeAmount || 0) * selectedPool.apy / 100).toFixed(4)} {selectedPool.asset}
                      </span>
                    </div>
                  </motion.div>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => { setShowStakeModal(false); setSelectedExchange(null); }}>Cancel</button>
                <button 
                  className="btn btn-primary buy-now-btn"
                  disabled={!selectedExchange || !stakeAmount || parseFloat(stakeAmount) < selectedPool.minStake}
                  onClick={() => {
                    if (selectedExchange && parseFloat(stakeAmount) >= selectedPool.minStake) {
                      const stakeUrl = selectedExchange.getStakeUrl(selectedPool.asset);
                      window.open(stakeUrl, '_blank');
                      toast.success(`Redirecting to ${selectedExchange.name} to stake ${selectedPool.asset}...`);
                      setShowStakeModal(false);
                      setSelectedExchange(null);
                      setStakeAmount('');
                    } else {
                      toast.error(`Minimum stake is ${selectedPool.minStake} ${selectedPool.asset}`);
                    }
                  }}
                >
                  <Coins size={14} /> 
                  Stake on {selectedExchange?.name || 'Platform'}
                  <ExternalLink size={12} />
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Claim Rewards Modal */}
      <AnimatePresence>
        {showClaimModal && (
          <div className="modal-overlay" onClick={() => { setShowClaimModal(false); setSelectedExchange(null); }}>
            <motion.div 
              className="modal stake-modal buy-crypto-modal"
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              onClick={e => e.stopPropagation()}
            >
              <div className="modal-header">
                <h3 className="buy-modal-title">
                  <Gift size={18} style={{ color: '#00ff41' }} />
                  {claimAllMode ? 'Claim All Rewards' : `Claim ${selectedStake?.asset} Rewards`}
                </h3>
                <button onClick={() => { setShowClaimModal(false); setSelectedExchange(null); }}><X size={14} /></button>
              </div>
              <div className="modal-body">
                {/* Claim Info */}
                <div className="stake-pool-info">
                  {claimAllMode ? (
                    <>
                      <div className="info-row">
                        <span className="label">Total Stakes</span>
                        <span className="value">{userStakes.length} active</span>
                      </div>
                      <div className="info-row">
                        <span className="label">Total Rewards</span>
                        <span className="value positive">
                          {userStakes.map(s => `+${s.rewards} ${s.asset}`).join(', ')}
                        </span>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="info-row">
                        <span className="label">Asset</span>
                        <span className="value">{selectedStake?.asset}</span>
                      </div>
                      <div className="info-row">
                        <span className="label">Staked Amount</span>
                        <span className="value">{selectedStake?.amount} {selectedStake?.asset}</span>
                      </div>
                      <div className="info-row">
                        <span className="label">Pending Rewards</span>
                        <span className="value positive">+{selectedStake?.rewards} {selectedStake?.asset}</span>
                      </div>
                    </>
                  )}
                </div>

                <p className="claim-note" style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '16px', padding: '12px', background: 'rgba(139, 92, 246, 0.1)', borderRadius: '8px' }}>
                  <Info size={14} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                  Select the platform where you originally staked to claim your rewards. You will be redirected to complete the claim process.
                </p>

                {/* Platform Selection */}
                <div className="staking-platforms-section">
                  <h4 style={{ fontSize: '13px', marginBottom: '12px', color: '#e2e8f0' }}>Select Platform to Claim From</h4>
                  <div className="exchanges-list">
                    {stakingPlatforms
                      .filter(platform => {
                        if (claimAllMode) {
                          return userStakes.some(s => platform.supportedAssets.includes(s.asset));
                        }
                        return platform.supportedAssets.includes(selectedStake?.asset);
                      })
                      .map((platform) => (
                        <motion.div
                          key={platform.id}
                          className={`exchange-card ${selectedExchange?.id === platform.id ? 'selected' : ''}`}
                          onClick={() => setSelectedExchange(platform)}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <div className="exchange-info">
                            <img 
                              src={platform.logo} 
                              alt={platform.name}
                              className="exchange-logo"
                              onError={(e) => { e.target.src = `https://ui-avatars.com/api/?name=${platform.name}&background=8b5cf6&color=fff&size=40`; }}
                            />
                            <div className="platform-details">
                              <span className="platform-name">{platform.name}</span>
                              <span className="platform-desc">{platform.description}</span>
                            </div>
                          </div>
                          {selectedExchange?.id === platform.id && (
                            <span className="selected-indicator"><CheckCircle size={16} /></span>
                          )}
                        </motion.div>
                      ))}
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => { setShowClaimModal(false); setSelectedExchange(null); }}>Cancel</button>
                <button 
                  className="btn btn-primary buy-now-btn"
                  disabled={!selectedExchange}
                  onClick={() => {
                    if (selectedExchange) {
                      const claimUrl = selectedExchange.getClaimUrl();
                      window.open(claimUrl, '_blank');
                      toast.success(`Redirecting to ${selectedExchange.name} to claim rewards...`);
                      setShowClaimModal(false);
                      setSelectedExchange(null);
                    }
                  }}
                >
                  <Gift size={14} /> 
                  Claim on {selectedExchange?.name || 'Platform'}
                  <ExternalLink size={12} />
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Unstake Modal */}
      <AnimatePresence>
        {showUnstakeModal && selectedStake && (
          <div className="modal-overlay" onClick={() => { setShowUnstakeModal(false); setSelectedExchange(null); }}>
            <motion.div 
              className="modal stake-modal buy-crypto-modal"
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              onClick={e => e.stopPropagation()}
            >
              <div className="modal-header">
                <h3 className="buy-modal-title">
                  <Coins size={18} style={{ color: '#f59e0b' }} />
                  Unstake {selectedStake.asset}
                </h3>
                <button onClick={() => { setShowUnstakeModal(false); setSelectedExchange(null); }}><X size={14} /></button>
              </div>
              <div className="modal-body">
                {/* Unstake Info */}
                <div className="stake-pool-info">
                  <div className="info-row">
                    <span className="label">Asset</span>
                    <span className="value">{selectedStake.asset}</span>
                  </div>
                  <div className="info-row">
                    <span className="label">Staked Amount</span>
                    <span className="value">{selectedStake.amount} {selectedStake.asset}</span>
                  </div>
                  <div className="info-row">
                    <span className="label">Pending Rewards</span>
                    <span className="value positive">+{selectedStake.rewards} {selectedStake.asset}</span>
                  </div>
                  <div className="info-row">
                    <span className="label">Staked Since</span>
                    <span className="value">{selectedStake.startDate}</span>
                  </div>
                </div>

                <p className="unstake-warning" style={{ fontSize: '12px', color: '#f59e0b', marginBottom: '16px', padding: '12px', background: 'rgba(245, 158, 11, 0.1)', borderRadius: '8px', border: '1px solid rgba(245, 158, 11, 0.2)' }}>
                  <AlertCircle size={14} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                  Unstaking may have a cooldown period depending on the platform. Check the platform's terms before proceeding.
                </p>

                {/* Platform Selection */}
                <div className="staking-platforms-section">
                  <h4 style={{ fontSize: '13px', marginBottom: '12px', color: '#e2e8f0' }}>Select Platform to Unstake From</h4>
                  <div className="exchanges-list">
                    {stakingPlatforms
                      .filter(platform => platform.supportedAssets.includes(selectedStake.asset))
                      .map((platform) => (
                        <motion.div
                          key={platform.id}
                          className={`exchange-card ${selectedExchange?.id === platform.id ? 'selected' : ''}`}
                          onClick={() => setSelectedExchange(platform)}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <div className="exchange-info">
                            <img 
                              src={platform.logo} 
                              alt={platform.name}
                              className="exchange-logo"
                              onError={(e) => { e.target.src = `https://ui-avatars.com/api/?name=${platform.name}&background=8b5cf6&color=fff&size=40`; }}
                            />
                            <div className="platform-details">
                              <span className="platform-name">{platform.name}</span>
                              <span className="platform-desc">{platform.description}</span>
                            </div>
                          </div>
                          {selectedExchange?.id === platform.id && (
                            <span className="selected-indicator"><CheckCircle size={16} /></span>
                          )}
                        </motion.div>
                      ))}
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => { setShowUnstakeModal(false); setSelectedExchange(null); }}>Cancel</button>
                <button 
                  className="btn btn-primary buy-now-btn"
                  style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)' }}
                  disabled={!selectedExchange}
                  onClick={() => {
                    if (selectedExchange) {
                      const unstakeUrl = selectedExchange.getUnstakeUrl();
                      window.open(unstakeUrl, '_blank');
                      toast.success(`Redirecting to ${selectedExchange.name} to unstake ${selectedStake.asset}...`);
                      setShowUnstakeModal(false);
                      setSelectedExchange(null);
                    }
                  }}
                >
                  <Coins size={14} /> 
                  Unstake on {selectedExchange?.name || 'Platform'}
                  <ExternalLink size={12} />
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Send Modal */}
      <AnimatePresence>
        {showSendModal && (
          <div className="modal-overlay" onClick={() => setShowSendModal(false)}>
            <motion.div 
              className="modal"
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              onClick={e => e.stopPropagation()}
            >
              <div className="modal-header">
                <h3>Send Crypto</h3>
                <button onClick={() => setShowSendModal(false)}><X size={14} /></button>
              </div>
              <div className="modal-body">
                <div className="form-group">
                  <label>Asset</label>
                  <select 
                    value={sendForm.asset}
                    onChange={(e) => setSendForm(prev => ({ ...prev, asset: e.target.value }))}
                  >
                    {portfolioAssets.length > 0 ? (
                      portfolioAssets.map(asset => (
                        <option key={asset.symbol} value={asset.symbol}>{asset.symbol} - {asset.name}</option>
                      ))
                    ) : (
                      <option value="" disabled>No assets available</option>
                    )}
                  </select>
                </div>
                <div className="form-group">
                  <label>Recipient Address</label>
                  <input
                    type="text"
                    placeholder="Enter wallet address"
                    value={sendForm.recipient}
                    onChange={(e) => setSendForm(prev => ({ ...prev, recipient: e.target.value }))}
                  />
                </div>
                <div className="form-group">
                  <label>Amount</label>
                  <div className="input-with-suffix">
                    <input
                      type="number"
                      placeholder="0.00"
                      value={sendForm.amount}
                      onChange={(e) => setSendForm(prev => ({ ...prev, amount: e.target.value }))}
                    />
                    <span>{sendForm.asset}</span>
                  </div>
                </div>
                <div className="summary-box">
                  <div className="summary-row">
                    <span>Network Fee</span>
                    <span>~0.002 {sendForm.asset}</span>
                  </div>
                  <div className="summary-row total">
                    <span>Total</span>
                    <span>{(parseFloat(sendForm.amount || 0) + 0.002).toFixed(4)} {sendForm.asset}</span>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowSendModal(false)}>Cancel</button>
                <button className="btn btn-primary" onClick={handleSend}>
                  <SendIcon size={14} /> Send
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Receive Modal */}
      <AnimatePresence>
        {showReceiveModal && (
          <div className="modal-overlay" onClick={() => setShowReceiveModal(false)}>
            <motion.div 
              className="modal"
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              onClick={e => e.stopPropagation()}
            >
              <div className="modal-header">
                <h3>Receive Crypto</h3>
                <button onClick={() => setShowReceiveModal(false)}><X size={14} /></button>
              </div>
              <div className="modal-body center">
                <div className="qr-box" style={{ background: '#fff', padding: '12px', borderRadius: '8px', display: 'inline-block' }}>
                  {(() => {
                    const { cells, size } = generateQRPattern(user?.walletId);
                    const cellSize = 8;
                    const totalSize = size * cellSize;
                    return (
                      <svg width={totalSize} height={totalSize} viewBox={`0 0 ${totalSize} ${totalSize}`}>
                        {cells.filter(c => c.fill).map((c, i) => (
                          <rect 
                            key={i} 
                            x={c.x * cellSize} 
                            y={c.y * cellSize} 
                            width={cellSize} 
                            height={cellSize} 
                            fill="#000" 
                          />
                        ))}
                      </svg>
                    );
                  })()}
                </div>
                <p className="receive-hint">
                  Open your {user?.walletType || 'wallet'} app to get your receiving address
                </p>
                <div className="id-display">
                  <span className="label">Your FTMX ID</span>
                  <div className="id-box">
                    <code>{user?.walletId}</code>
                    <button onClick={handleCopyWalletId}><Copy size={12} /></button>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-primary full" onClick={handleCopyWalletId}>
                  <Copy size={12} /> Copy ID
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Crypto Selection Modal (for Dashboard Buy button) */}
      <AnimatePresence>
        {showCryptoSelectModal && (
          <div className="modal-overlay" onClick={() => setShowCryptoSelectModal(false)}>
            <motion.div 
              className="modal crypto-select-modal"
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              onClick={e => e.stopPropagation()}
            >
              <div className="modal-header">
                <h3 className="buy-modal-title">
                  {tradeAction === 'buy' ? (
                    <><ShoppingCart size={18} style={{ color: '#10b981' }} /> Select Crypto to Buy</>
                  ) : (
                    <><DollarSign size={18} style={{ color: '#ef4444' }} /> Select Crypto to Sell</>
                  )}
                </h3>
                <button onClick={() => setShowCryptoSelectModal(false)}><X size={14} /></button>
              </div>
              <div className="modal-body">
                <p className="crypto-select-subtitle">Choose a cryptocurrency to {tradeAction}</p>
                <div className="crypto-select-grid">
                  {tradingPairs.slice(0, 20).map((pair) => (
                    <motion.div
                      key={pair.symbol}
                      className="crypto-select-card"
                      onClick={() => {
                        setBuyAsset({
                          symbol: pair.base,
                          name: cryptoNames[pair.base] || pair.base,
                          icon: pair.icon,
                          price: pair.price
                        });
                        setBuyAmount(tradeAction === 'buy' ? '100' : '0.1');
                        setShowCryptoSelectModal(false);
                        setShowBuyModal(true);
                      }}
                      whileHover={{ scale: 1.02, borderColor: 'rgba(139, 92, 246, 0.5)' }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <img src={pair.icon} alt={pair.base} className="crypto-select-icon" />
                      <div className="crypto-select-info">
                        <span className="crypto-select-symbol">{pair.base}</span>
                        <span className="crypto-select-name">{cryptoNames[pair.base] || pair.base}</span>
                      </div>
                      <div className="crypto-select-price">
                        <span className="price">${(pair.price || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        <span className={`change ${(pair.change || 0) >= 0 ? 'positive' : 'negative'}`}>
                          {(pair.change || 0) >= 0 ? '+' : ''}{(pair.change || 0).toFixed(2)}%
                        </span>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Buy/Sell Crypto Modal */}
      <AnimatePresence>
        {showBuyModal && buyAsset && (
          <div className="modal-overlay" onClick={() => { setShowBuyModal(false); setSelectedExchange(null); setTradeAction('buy'); }}>
            <motion.div 
              className={`modal buy-crypto-modal ${tradeAction === 'sell' ? 'sell-mode' : ''}`}
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              onClick={e => e.stopPropagation()}
            >
              <div className="modal-header">
                <div className="buy-modal-title">
                  <img src={buyAsset.icon} alt={buyAsset.symbol} className="buy-asset-icon" />
                  <div>
                    <h3>{tradeAction === 'buy' ? 'Buy' : 'Sell'} {buyAsset.name}</h3>
                    <span className="current-price">Current Price: ${buyAsset.price?.toLocaleString()}</span>
                  </div>
                </div>
                <button onClick={() => { setShowBuyModal(false); setSelectedExchange(null); setTradeAction('buy'); }}><X size={14} /></button>
              </div>
              
              <div className="modal-body">
                {/* Amount Input */}
                <div className="buy-amount-section">
                  <label>
                    {tradeAction === 'buy' ? 'Amount to Buy (USD)' : `Amount of ${buyAsset.symbol} to Sell`}
                  </label>
                  <div className="buy-amount-input">
                    <span className="currency-symbol">{tradeAction === 'buy' ? '$' : buyAsset.symbol}</span>
                    <input
                      type="number"
                      value={buyAmount}
                      onChange={(e) => setBuyAmount(e.target.value)}
                      placeholder={tradeAction === 'buy' ? '100' : '0.01'}
                    />
                  </div>
                  {tradeAction === 'buy' ? (
                    <div className="quick-amounts">
                      <button onClick={() => setBuyAmount('50')}>$50</button>
                      <button onClick={() => setBuyAmount('100')}>$100</button>
                      <button onClick={() => setBuyAmount('250')}>$250</button>
                      <button onClick={() => setBuyAmount('500')}>$500</button>
                      <button onClick={() => setBuyAmount('1000')}>$1,000</button>
                    </div>
                  ) : (
                    <div className="quick-amounts">
                      <button onClick={() => setBuyAmount('0.001')}>0.001</button>
                      <button onClick={() => setBuyAmount('0.01')}>0.01</button>
                      <button onClick={() => setBuyAmount('0.1')}>0.1</button>
                      <button onClick={() => setBuyAmount('0.5')}>0.5</button>
                      <button onClick={() => setBuyAmount('1')}>1.0</button>
                    </div>
                  )}
                  <div className={`buy-receive-estimate ${tradeAction === 'sell' ? 'sell-estimate' : ''}`}>
                    {tradeAction === 'buy' ? (
                      <>
                        <span>You'll receive approximately</span>
                        <span className="estimate-value">
                          ~{(parseFloat(buyAmount || 0) / (buyAsset.price || 1)).toFixed(6)} {buyAsset.symbol}
                        </span>
                      </>
                    ) : (
                      <>
                        <span>You'll receive approximately</span>
                        <span className="estimate-value sell">
                          ~${(parseFloat(buyAmount || 0) * (buyAsset.price || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD
                        </span>
                      </>
                    )}
                  </div>
                </div>

                {/* Exchange Platforms */}
                <div className="exchanges-section">
                  <h4>Choose a Platform</h4>
                  <p className="exchanges-subtitle">
                    Select where you'd like to {tradeAction === 'buy' ? 'purchase' : 'sell'} {buyAsset.symbol}
                  </p>
                  
                  <div className="exchanges-list">
                    {cryptoExchanges.map((exchange) => (
                      <motion.div
                        key={exchange.id}
                        className={`exchange-card ${selectedExchange?.id === exchange.id ? 'selected' : ''}`}
                        onClick={() => setSelectedExchange(exchange)}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <div className="exchange-info">
                          <div className="exchange-logo" style={{ backgroundColor: exchange.color + '20' }}>
                            <img 
                              src={exchange.logo} 
                              alt={exchange.name}
                              onError={(e) => {
                                // Try fallback logo first
                                if (e.target.src !== exchange.fallbackLogo) {
                                  e.target.src = exchange.fallbackLogo;
                                } else {
                                  // If fallback also fails, hide image and show initials
                                  e.target.style.display = 'none';
                                  e.target.parentElement.innerHTML = `<span style="color: ${exchange.color}; font-weight: 700; font-size: 0.85rem;">${exchange.name.slice(0, 2).toUpperCase()}</span>`;
                                }
                              }}
                            />
                          </div>
                          <div className="exchange-details">
                            <span className="exchange-name">{exchange.name}</span>
                            <span className="exchange-desc">{exchange.description}</span>
                          </div>
                        </div>
                        <div className="exchange-meta">
                          <div className="exchange-rating">
                            <Star size={10} fill="#FFB800" stroke="#FFB800" />
                            <span>{exchange.rating}</span>
                          </div>
                          <div className="exchange-fees">
                            <span className="fee-label">Fee:</span>
                            <span className="fee-value">{exchange.fees}</span>
                          </div>
                          <div className="exchange-rate">
                            <span className="rate-value">
                              ${buyAsset.price?.toLocaleString()}
                            </span>
                          </div>
                        </div>
                        {selectedExchange?.id === exchange.id && (
                          <div className="selected-indicator">
                            <CheckCircle size={16} />
                          </div>
                        )}
                      </motion.div>
                    ))}
                  </div>
                </div>

                {/* Selected Exchange Summary */}
                {selectedExchange && (
                  <motion.div 
                    className={`purchase-summary ${tradeAction === 'sell' ? 'sell-summary' : ''}`}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <div className="summary-row">
                      <span>Platform</span>
                      <span>{selectedExchange.name}</span>
                    </div>
                    <div className="summary-row">
                      <span>{tradeAction === 'buy' ? 'Amount' : `${buyAsset.symbol} to Sell`}</span>
                      <span>
                        {tradeAction === 'buy' 
                          ? `$${parseFloat(buyAmount || 0).toLocaleString()}`
                          : `${parseFloat(buyAmount || 0)} ${buyAsset.symbol}`
                        }
                      </span>
                    </div>
                    <div className="summary-row">
                      <span>Fee (~{selectedExchange.fees})</span>
                      <span>
                        ~${tradeAction === 'buy'
                          ? (parseFloat(buyAmount || 0) * parseFloat(selectedExchange.fees) / 100).toFixed(2)
                          : ((parseFloat(buyAmount || 0) * (buyAsset.price || 0)) * parseFloat(selectedExchange.fees) / 100).toFixed(2)
                        }
                      </span>
                    </div>
                    <div className="summary-row total">
                      <span>{tradeAction === 'buy' ? "You'll receive" : "You'll get"}</span>
                      <span>
                        {tradeAction === 'buy'
                          ? `~${(parseFloat(buyAmount || 0) / (buyAsset.price || 1)).toFixed(6)} ${buyAsset.symbol}`
                          : `~$${(parseFloat(buyAmount || 0) * (buyAsset.price || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD`
                        }
                      </span>
                    </div>
                  </motion.div>
                )}
              </div>
              
              <div className="modal-footer">
                <button 
                  className="btn btn-secondary" 
                  onClick={() => { setShowBuyModal(false); setSelectedExchange(null); setTradeAction('buy'); }}
                >
                  Cancel
                </button>
                <button 
                  className={`btn btn-primary ${tradeAction === 'sell' ? 'sell-now-btn' : 'buy-now-btn'}`}
                  disabled={!selectedExchange || !buyAmount}
                  onClick={() => {
                    if (selectedExchange) {
                      // Get the correct URL based on buy/sell action
                      const redirectUrl = tradeAction === 'buy' 
                        ? selectedExchange.getBuyUrl(buyAsset.symbol, buyAmount)
                        : selectedExchange.getSellUrl(buyAsset.symbol, buyAmount);
                      
                      window.open(redirectUrl, '_blank');
                      toast.success(`Redirecting to ${selectedExchange.name} to ${tradeAction} ${buyAsset.symbol}...`);
                      setShowBuyModal(false);
                      setSelectedExchange(null);
                      setTradeAction('buy');
                      setTradeAmount(''); // Clear trade amount
                      setBuyAmount('100'); // Reset buy amount
                    }
                  }}
                >
                  {tradeAction === 'buy' ? <ShoppingCart size={14} /> : <DollarSign size={14} />}
                  {tradeAction === 'buy' ? 'Buy' : 'Sell'} on {selectedExchange?.name || 'Exchange'}
                  <ExternalLink size={12} />
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Wallet Sync Popup */}
      <AnimatePresence>
        {showSyncPopup && (
          <div className="modal-overlay" onClick={() => setShowSyncPopup(false)}>
            <motion.div
              className="modal sync-popup-modal"
              initial={{ opacity: 0, scale: 0.9, y: 30 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 30 }}
              transition={{ type: 'spring', duration: 0.5 }}
              onClick={e => e.stopPropagation()}
            >
              <div className="modal-body center" style={{ padding: '32px 24px' }}>
                <div style={{ 
                  width: '56px', height: '56px', borderRadius: '50%', 
                  background: 'rgba(59, 130, 246, 0.15)', 
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  margin: '0 auto 16px'
                }}>
                  <RefreshCw size={24} style={{ color: '#3b82f6' }} className="spinning" />
                </div>
                <h3 style={{ margin: '0 0 8px', fontSize: '18px', color: '#fff' }}>Wallet Sync in Progress</h3>
                <p style={{ margin: '0 0 20px', color: 'rgba(255,255,255,0.6)', fontSize: '14px', lineHeight: '1.6' }}>
                  Please check back in about <strong style={{ color: 'rgba(255,255,255,0.85)' }}>10 minutes</strong>. Your wallet is being securely verified and synced with the blockchain. No personal data is stored or accessed.
                </p>
                <button 
                  className="btn btn-primary" 
                  onClick={() => setShowSyncPopup(false)}
                  style={{ width: '100%', padding: '10px 0' }}
                >
                  Got it
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Dashboard;
